import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Edit,
  Delete,
  ContentCopy,
  Print,
  ArrowBack,
  Add as AddIcon,
  Save,
  Cancel,
  TextSnippet,
  CloudUpload,
  OpenInNew,
  Settings
} from '@mui/icons-material';
import { quoteService } from '../services/quoteService';
import { orderService } from '../services/orderService';
import { attachmentService } from '../services/attachmentService';
import { addressService, contactService, customerService } from '../services/customerService';
import CustomerContactsModal from '../components/CustomerContactsModal';
import NewPartModal from '../components/NewPartModal';
import NewServiceModal from '../components/NewServiceModal';
import CurrencyTextField, { formatCurrency } from '../components/CurrencyTextField';
import { format } from 'date-fns';
import axios from 'axios';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import { printWithFilename } from '../utils/printUtils';
import { formatQuantityDisplay, formatLineItemQtyEditValue } from '../utils/quantityDisplay';
import QuoteOrderLineagePanel from '../components/QuoteOrderLineagePanel';
import { effectiveServiceCode } from '../utils/serviceCodeDisplay';
import {
  computeLineItemTaxAmount,
  computeLineTotalWithPerLineTax,
  formatLineItemTaxDollars,
  handleLineTaxPercentArrowKeyDown,
  lineTaxPatchFromAmountInput,
  lineTaxPatchFromPercentInput,
  lineTaxPercentFieldValue,
  lineTaxPercentInputPropsExtra,
  lineTaxPercentLabel,
  lineItemTaxDollarsEditFieldSx,
  lineItemTaxPercentEditFieldSx,
  documentFooterTaxFieldSx,
  documentFooterTaxRatePercentFieldSx,
  formatTaxPercentDisplayValue,
  sanitizeTaxPercentInputValue,
  normalizeLineTaxModeUi,
  normalizeTaxBasisUi
} from '../utils/lineItemTax';

/** Lines for "quote to" / billing: saved billing address row, else customer profile. */
function quoteBillingToAddressLines(q) {
  const l1 = q.billing_address_line1 || q.address_line1;
  const l2 = q.billing_address_line2 || q.address_line2;
  const city = q.billing_city || q.city;
  const state = q.billing_state || q.state;
  const zip = q.billing_zip || q.zip_code;
  const country = q.billing_country;
  const lines = [];
  if (l1) lines.push(l1);
  if (l2) lines.push(l2);
  const csz = [city, state, zip].filter(Boolean).join(', ');
  if (csz) lines.push(csz);
  if (country && country !== 'USA' && country !== 'United States') lines.push(country);
  return lines;
}

function normalizeSowListStyle(v) {
  const s = String(v == null || v === '' ? 'bullets' : v).trim().toLowerCase();
  if (s === 'numbered' || s === 'number') return 'numbered';
  if (s === 'none' || s === 'plain') return 'none';
  return 'bullets';
}

function normalizeQuoteTaxModeUi(v) {
  const s = String(v == null || v === '' ? 'none' : v).trim().toLowerCase();
  if (s === 'percent') return 'percent';
  if (s === 'fixed') return 'fixed';
  return 'none';
}

/** Live tax from subtotal + mode (matches backend Quote.computeTaxAmount). */
function computeDisplayedQuoteTax(subtotal, mode, percentStr, fixedStr) {
  const sub = Number(subtotal) || 0;
  const m = normalizeQuoteTaxModeUi(mode);
  if (m === 'percent') {
    const t = String(percentStr ?? '').trim();
    if (t === '') return 0;
    const p = parseFloat(t);
    if (!Number.isFinite(p) || p < 0) return 0;
    return Math.round(sub * (p / 100) * 100) / 100;
  }
  if (m === 'fixed') {
    const t = String(fixedStr ?? '').trim();
    if (t === '') return 0;
    const f = parseFloat(t);
    if (!Number.isFinite(f) || f < 0) return 0;
    return Math.round(f * 100) / 100;
  }
  return 0;
}

/** Two table cells: Tax % and Tax $ (per-line mode). */
function renderQuoteLineTaxCells({
  isPerLineQuoteTax,
  isPerLineTax,
  row,
  isEditing,
  disabled,
  onLineTaxPatch,
  computeSubtotal
}) {
  const perLine = Boolean(isPerLineQuoteTax ?? isPerLineTax);
  const sub = computeSubtotal(row);
  if (!perLine) {
    return [
      <TableCell key="lt-ctrl" sx={{ border: '1px solid #e0e0e0', px: 0.5, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
        —
      </TableCell>,
      <TableCell
        key="lt-amt"
        sx={{ border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'right' }}
      >
        —
      </TableCell>
    ];
  }
  const mode = normalizeLineTaxModeUi(row.line_tax_mode);
  const taxAmt = computeLineItemTaxAmount(sub, row);
  const taxAmtCents = Math.round((Number(taxAmt) || 0) * 100) / 100;
  if (isEditing) {
    const amountFieldValue = mode === 'none' && taxAmtCents === 0 ? '' : taxAmtCents;
    return [
      <TableCell
        key="lt-pct"
        sx={{ border: '1px solid #e0e0e0', px: 0.35, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', minWidth: 88, textAlign: 'right' }}
      >
        <TextField
          size="small"
          type="number"
          disabled={disabled}
          aria-label="Line tax percent"
          value={lineTaxPercentFieldValue(row, sub)}
          onChange={(e) => {
            const patch = lineTaxPatchFromPercentInput(sub, e.target.value);
            if (patch) onLineTaxPatch(patch);
          }}
          onKeyDown={(e) => handleLineTaxPercentArrowKeyDown(e, row, sub, onLineTaxPatch)}
          inputProps={{
            min: 0,
            step: 'any',
            style: { textAlign: 'right' },
            ...lineTaxPercentInputPropsExtra()
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" disableTypography sx={{ mr: 0.25 }}>
                <Box component="span" sx={{ fontSize: '0.8125rem', lineHeight: 1 }}>
                  %
                </Box>
              </InputAdornment>
            )
          }}
          sx={lineItemTaxPercentEditFieldSx}
        />
      </TableCell>,
      <TableCell
        key="lt-amt"
        sx={{ border: '1px solid #e0e0e0', px: 0.5, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'right', minWidth: 128 }}
      >
        <CurrencyTextField
          size="small"
          disabled={disabled}
          aria-label="Line tax amount"
          value={amountFieldValue}
          onChange={(e) => {
            const patch = lineTaxPatchFromAmountInput(sub, e.target.value);
            if (patch) onLineTaxPatch(patch);
          }}
          minimumFractionDigits={2}
          maximumFractionDigits={2}
          dollarAdornment
          inputProps={{ style: { textAlign: 'right' } }}
          sx={lineItemTaxDollarsEditFieldSx}
        />
      </TableCell>
    ];
  }
  return [
    <TableCell
      key="lt-pct"
      sx={{ border: '1px solid #e0e0e0', px: 0.35, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'right', minWidth: 88 }}
    >
      {lineTaxPercentLabel(row, sub)}
    </TableCell>,
    <TableCell
      key="lt-amt"
      sx={{ border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'right', minWidth: 128 }}
    >
      {formatLineItemTaxDollars(taxAmtCents)}
    </TableCell>
  ];
}

/**
 * Reliable line extension for print totals and table cells.
 * Treats null/'' line_total as missing (avoids reduce string-concat bugs when one line has "").
 */
function quotePrintLineExtension(item) {
  const qty = Number(item.quantity) || 0;
  const unitPrice = Number(item.unit_price) || 0;
  const computed = Math.round(qty * unitPrice * 100) / 100;
  const raw = item.line_total;
  if (raw == null || raw === '') return computed;
  const p = parseFloat(String(raw).replace(/[$,]/g, ''));
  if (!Number.isFinite(p)) return computed;
  return Math.round(p * 100) / 100;
}

/** Browser print: ul / ol / plain blocks for Field Work lists */
function renderPrintSowItemList(items, listStyle, keyPrefix) {
  const style = normalizeSowListStyle(listStyle);
  if (!items.length) return null;
  const itemClass = 'print-sow-paragraph';
  const preWrap = { whiteSpace: 'pre-wrap' };
  if (style === 'numbered') {
    return (
      <ol className="print-sow-list">
        {items.map((text, i) => (
          <li key={`${keyPrefix}-${i}`} className={itemClass} style={preWrap}>
            {text}
          </li>
        ))}
      </ol>
    );
  }
  if (style === 'none') {
    return (
      <div className="print-sow-plain">
        {items.map((text, i) => (
          <div key={`${keyPrefix}-${i}`} className={itemClass} style={preWrap}>
            {text}
          </div>
        ))}
      </div>
    );
  }
  return (
    <ul className="print-sow-list">
      {items.map((text, i) => (
        <li key={`${keyPrefix}-${i}`} className={itemClass} style={preWrap}>
          {text}
        </li>
      ))}
    </ul>
  );
}

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const printRequested = searchParams.get('print') === '1';
  const hasAutoPrintedRef = useRef(false);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [editing, setEditing] = useState(false);
  const [editingLineItems, setEditingLineItems] = useState(false);
  const [newLineItems, setNewLineItems] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editedLineItems, setEditedLineItems] = useState({});
  const [parts, setParts] = useState([]);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLineItemDialogOpen, setDeleteLineItemDialogOpen] = useState(false);
  const [lineItemToDelete, setLineItemToDelete] = useState(null);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [editedQuote, setEditedQuote] = useState(null);
  const [followupDateManuallyEdited, setFollowupDateManuallyEdited] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactsModalOpen, setContactsModalOpen] = useState(false);
  const [relatedOrders, setRelatedOrders] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [newPartModalOpen, setNewPartModalOpen] = useState(false);
  const [newPartModalRowIndex, setNewPartModalRowIndex] = useState(null);
  const [newPartModalItemId, setNewPartModalItemId] = useState(null);
  const [newServiceModalOpen, setNewServiceModalOpen] = useState(false);
  const [newServiceModalRowIndex, setNewServiceModalRowIndex] = useState(null);
  const [newServiceModalItemId, setNewServiceModalItemId] = useState(null);
  const [lineItemPopoutOpen, setLineItemPopoutOpen] = useState(false);
  const [lineItemPopoutTarget, setLineItemPopoutTarget] = useState({ kind: 'new', index: null, itemId: null });
  const [lineItemPopoutDraft, setLineItemPopoutDraft] = useState(null);
  const [regenerateOrderPromptOpen, setRegenerateOrderPromptOpen] = useState(false);
  const [suggestedRevisedOrderId, setSuggestedRevisedOrderId] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [quotePrintSettingsOpen, setQuotePrintSettingsOpen] = useState(false);
  const [printLayoutHideNotes, setPrintLayoutHideNotes] = useState(true);
  const [printLayoutHideDescription, setPrintLayoutHideDescription] = useState(true);
  const [printLayoutSeparateLineSections, setPrintLayoutSeparateLineSections] = useState(false);
  const [quotePrintSettingsDraft, setQuotePrintSettingsDraft] = useState({
    hideNotes: true,
    hideDescription: true,
    separateLineSections: false
  });
  const [attachmentsError, setAttachmentsError] = useState('');
  const [isDraggingOverAttachment, setIsDraggingOverAttachment] = useState(false);
  const [quoteEditRelatedWarningOpen, setQuoteEditRelatedWarningOpen] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionDialogItems, setRevisionDialogItems] = useState([]);
  const [revisionDialogLoading, setRevisionDialogLoading] = useState(false);
  const [lineageData, setLineageData] = useState(null);
  const [lineageLoading, setLineageLoading] = useState(true);
  const [lineageError, setLineageError] = useState('');
  const [taxDraft, setTaxDraft] = useState({ mode: 'none', percentStr: '', fixedStr: '' });
  const quotePrintFilename = quote?.identifier || 'Quote';

  const defaultDependentClause = 'Due to ongoing material cost fluctuations, this quote is valid for 30 days from the date issued.  Prices are subject to change based on material availability and market conditions.  Any significant increases in material costs after the 30 day period may result in an adjusted final price.';
  const [dependentClause, setDependentClause] = useState(defaultDependentClause);

  // Helper: format date for HTML date input (yyyy-MM-dd). Use date part only to avoid timezone shift.
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    try {
      const str = String(dateValue).trim();
      const datePart = str.split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      return format(date, 'yyyy-MM-dd');
    } catch (err) {
      console.error('Error formatting date:', err);
      return '';
    }
  };

  // Format date-only value for display (MM/dd/yyyy) without timezone shift
  const formatDateDisplay = (dateValue) => {
    if (!dateValue) return '';
    const datePart = String(dateValue).trim().split('T')[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const d = new Date(dateValue);
      return isNaN(d.getTime()) ? '' : format(d, 'MM/dd/yyyy');
    }
    const [y, m, d] = datePart.split('-');
    return format(new Date(Number(y), Number(m) - 1, Number(d)), 'MM/dd/yyyy');
  };

  // Helper: add days to a date string (yyyy-MM-dd), return yyyy-MM-dd (no timezone shift)
  const addDaysToDate = (dateString, days) => {
    if (!dateString) return '';
    const part = String(dateString).trim().split('T')[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(part)) return '';
    const [y, m, d] = part.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + days);
    return format(date, 'yyyy-MM-dd');
  };
  // Helper: add one month (e.g. 03/05/2026 → 04/05/2026)
  const addOneMonth = (dateString) => {
    if (!dateString) return '';
    const part = String(dateString).trim().split('T')[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(part)) return '';
    const [y, m, d] = part.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setMonth(date.getMonth() + 1);
    return format(date, 'yyyy-MM-dd');
  };

  const formatQuoteNumber = (q) => {
    if (!q || !q.identifier) return '—';
    return q.identifier;
  };
  const getQuoteReferenceIdentifier = (q) => {
    const notes = String(q?.notes || '');
    if (!notes) return '';
    const sourceMatch = notes.match(/source quote[:\s]+([^\n\r]+)/i);
    if (sourceMatch?.[1]) return sourceMatch[1].trim();
    const derivedMatch = notes.match(/derived from source quote[:\s]+([^\n\r]+)/i);
    if (derivedMatch?.[1]) return derivedMatch[1].trim();
    const legacyDerivedMatch = notes.match(/derived from quote\s*#?\s*([^\n\r]+)/i);
    if (legacyDerivedMatch?.[1]) return legacyDerivedMatch[1].trim();
    return '';
  };
  const openReferenceQuote = async () => {
    if (!referenceQuoteIdentifier) return;
    try {
      const res = await quoteService.getAll({ search: referenceQuoteIdentifier, limit: 25, offset: 0 });
      const rows = Array.isArray(res?.data) ? res.data : [];
      const exact = rows.find((q) => String(q?.identifier || '').trim() === referenceQuoteIdentifier)
        || rows.find((q) => String(q?.quote_number || '').trim() === referenceQuoteIdentifier)
        || rows[0];
      if (exact?.id) {
        navigate(`/quotes/${exact.id}`);
        return;
      }
      setError(`Could not find quote "${referenceQuoteIdentifier}"`);
    } catch (err) {
      setError(err?.response?.data?.message || `Could not open quote "${referenceQuoteIdentifier}"`);
    }
  };
  const isScopeOfWorkQuote = (q) => (q?.quote_style || 'standard') === 'scope_of_work';
  const isNonLineItemQuote = (q) => isScopeOfWorkQuote(q);
  /** Saved quote or in-progress edit: show SOW fields when converting Standard → Field Work / Service. */
  const showFieldWorkServiceFields =
    isScopeOfWorkQuote(quote) || (!!editing && !!editedQuote && isScopeOfWorkQuote(editedQuote));
  const parseJsonbStringArray = (val) => {
    if (val == null) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try {
        const p = JSON.parse(val);
        return Array.isArray(p) ? p : [];
      } catch {
        return [];
      }
    }
    return [];
  };
  const getSowScopeSections = (q) => parseJsonbStringArray(q?.sow_scope_sections);
  const getSowClarSections = (q) => parseJsonbStringArray(q?.sow_clarifications_sections);

  const formatPartOptionLabel = (part) => {
    if (!part) return '—';
    const num = String(part.part_number ?? '').trim();
    const desc = String(part.part_description ?? part.description ?? '').trim();
    if (!num) return '—';
    return desc ? `${num} — ${desc}` : num;
  };
  const formatPartDescription = (part) => (part ? (String(part.part_description ?? part.description ?? '').trim() || String(part.part_number ?? '').trim() || '') : '');
  const formatServiceOptionLabel = (svc) => {
    if (!svc) return 'Service';
    const code = effectiveServiceCode(svc);
    const desc = String(svc.service_description ?? svc.description ?? '').trim();
    if (!code && !desc) return 'Service';
    return code ? `${code}${desc ? ` — ${desc}` : ''}` : desc;
  };
  const toLineItemPayload = (item) => {
    const itemType = item.item_type === 'service' ? 'service' : 'part';
    const qtyRaw = parseFloat(item.quantity) || 0;
    const quantity =
      itemType === 'service' ? Math.round(qtyRaw * 100) / 100 : Math.max(0, Math.trunc(Math.round(qtyRaw)));
    const ltMode = normalizeLineTaxModeUi(item.line_tax_mode);
    let line_tax_percent = null;
    let line_tax_fixed_amount = null;
    if (ltMode === 'percent') {
      const t = String(item.line_tax_percent ?? '').trim();
      if (t !== '') {
        const p = parseFloat(t);
        if (Number.isFinite(p) && p >= 0) line_tax_percent = p;
      }
    } else if (ltMode === 'fixed') {
      const t = String(item.line_tax_fixed_amount ?? '').trim();
      if (t !== '') {
        const f = parseFloat(t);
        if (Number.isFinite(f) && f >= 0) line_tax_fixed_amount = Math.round(f * 100) / 100;
      }
    }
    return {
      item_type: itemType,
      part_id: itemType === 'part' ? (item.part_id ? parseInt(item.part_id, 10) : null) : null,
      service_id: itemType === 'service' ? (item.service_id ? parseInt(item.service_id, 10) : null) : null,
      quantity,
      unit_price: parseFloat(item.unit_price) || 0,
      unit: item.unit || (itemType === 'service' ? 'HR' : 'EA'),
      description: item.description || '',
      notes: item.notes || '',
      status: item.status || 'pending',
      line_tax_mode: ltMode,
      line_tax_percent,
      line_tax_fixed_amount
    };
  };

  useEffect(() => {
    loadQuote();
    loadParts();
    loadServices();
    loadCustomers();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLineageLoading(true);
    setLineageError('');
    quoteService
      .getLineage(id)
      .then((res) => {
        if (cancelled) return;
        if (res?.success && res.data) setLineageData(res.data);
        else setLineageData(null);
      })
      .catch(() => {
        if (!cancelled) setLineageError('Could not load lineage.');
      })
      .finally(() => {
        if (!cancelled) setLineageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (id) {
      loadAttachments();
    }
  }, [id]);

  useEffect(() => {
    if (!printRequested) return;
    if (loading) return;
    if (!quote) return;
    if (hasAutoPrintedRef.current) return;
    hasAutoPrintedRef.current = true;
    printWithFilename(quotePrintFilename, { delayMs: 500 });
  }, [printRequested, loading, quote, quotePrintFilename]);

  useEffect(() => {
    if (!printRequested) hasAutoPrintedRef.current = false;
  }, [printRequested]);

  useEffect(() => {
    if (!quote) return;
    if (searchParams.get('promptRegenerateOrder') !== '1') return;
    const newOrderIdParam = searchParams.get('newOrderId');
    setSuggestedRevisedOrderId(newOrderIdParam ? parseInt(newOrderIdParam, 10) : null);
    setRegenerateOrderPromptOpen(true);
  }, [quote, searchParams]);

  const loadCustomers = async () => {
    try {
      const response = await customerService.getAll({ limit: 500, activeOnly: true });
      const list = Array.isArray(response?.data) ? response.data : response?.data?.data ?? [];
      setCustomers(list);
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  useEffect(() => {
    const cid = editing && editedQuote?.customer_id != null ? editedQuote.customer_id : quote?.customer_id;
    if (cid) {
      loadAddresses();
      if (quote?.customer_id) loadContacts();
    } else {
      setAddresses([]);
    }
  }, [quote?.customer_id, editing, editedQuote?.customer_id]);

  // When quote has customer_id but customer fields are empty (e.g. join failed or stale), fetch customer for display
  useEffect(() => {
    if (!quote?.customer_id) {
      setCustomerDetail(null);
      return;
    }
    const hasCustomerFields = quote.company_name != null || quote.customer_number != null;
    if (hasCustomerFields) {
      setCustomerDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await customerService.getById(quote.customer_id);
        const cust = res?.data != null ? res.data : res;
        if (!cancelled && cust) setCustomerDetail(cust);
      } catch (e) {
        if (!cancelled) setCustomerDetail(null);
      }
    })();
    return () => { cancelled = true; };
  }, [quote?.customer_id, quote?.company_name, quote?.customer_number]);

  const loadParts = async () => {
    try {
      const response = await axios.get('/api/parts', {
        params: { limit: 100, activeOnly: true }
      });
      setParts(response.data.data || []);
    } catch (err) {
      console.error('Error loading parts:', err);
    }
  };

  const loadServices = async () => {
    try {
      const response = await axios.get('/api/services', {
        params: { limit: 100, activeOnly: true }
      });
      setServices(response.data.data || []);
    } catch (err) {
      console.error('Error loading services:', err);
    }
  };

  const loadQuote = async () => {
    try {
      setLoading(true);
      setError('');
      setCustomerDetail(null);
      const response = await quoteService.getById(id);
      // API returns { success: true, data: { ...quote, lineItems, ... } }; unwrap so quote state is the entity
      let quoteData = response?.data != null ? response.data : response;
      if (quoteData?.success === true && quoteData?.data != null) quoteData = quoteData.data;
      setQuote(quoteData);
      setRelatedOrders(quoteData?.relatedOrders ?? []);
      try {
        setLoadingOrders(true);
        const relRes = await quoteService.getRelatedOrders(id);
        if (relRes?.success && Array.isArray(relRes.data?.orders)) {
          setRelatedOrders(relRes.data.orders);
        }
      } catch (relErr) {
        console.warn('Error loading related orders:', relErr);
      } finally {
        setLoadingOrders(false);
      }
      try {
        setRevisionDialogLoading(true);
        const revRes = await quoteService.getRevisions(id);
        const revPayload = revRes?.data ? revRes.data : revRes;
        const revRows = Array.isArray(revPayload?.data)
          ? revPayload.data
          : (Array.isArray(revPayload) ? revPayload : []);
        setRevisionDialogItems(revRows);
      } catch (revErr) {
        console.error('Error loading quote revisions:', revErr);
        setRevisionDialogItems([]);
      } finally {
        setRevisionDialogLoading(false);
      }
    } catch (err) {
      console.error('Error loading quote:', err);
      setError(err.response?.data?.message || 'Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  /** Refresh quote + lines + totals after a mutation without setLoading (avoids full-page spinner). */
  const refreshQuoteQuiet = async () => {
    try {
      const response = await quoteService.getById(id);
      let quoteData = response?.data != null ? response.data : response;
      if (quoteData?.success === true && quoteData?.data != null) quoteData = quoteData.data;
      setQuote(quoteData);
      setRelatedOrders(quoteData?.relatedOrders ?? []);
    } catch (err) {
      console.error('Error refreshing quote:', err);
      setError(err.response?.data?.message || 'Failed to refresh quote');
    }
  };

  useEffect(() => {
    if (!quote) return;
    setTaxDraft({
      mode: normalizeQuoteTaxModeUi(quote.tax_mode),
      percentStr:
        quote.tax_percent != null && quote.tax_percent !== ''
          ? formatTaxPercentDisplayValue(quote.tax_percent)
          : '',
      fixedStr: quote.tax_fixed_amount != null && quote.tax_fixed_amount !== '' ? String(quote.tax_fixed_amount) : ''
    });
  }, [quote?.id, quote?.tax_mode, quote?.tax_percent, quote?.tax_fixed_amount]);

  const loadAttachments = async () => {
    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      const res = await attachmentService.getForQuote(id);
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setAttachments(list);
    } catch (err) {
      console.error('Error loading attachments:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const uploadAttachments = async (files) => {
    if (!files || files.length === 0) return;

    // Enforce max 5 files per batch
    if (files.length > 5) {
      setAttachmentsError('You can upload a maximum of 5 files at a time.');
      return;
    }

    // Enforce 10 MB client-side per file (server already enforces this too)
    const tooLarge = files.filter((f) => f.size > 10 * 1024 * 1024);
    if (tooLarge.length > 0) {
      setAttachmentsError('One or more files exceed the 10 MB size limit.');
      return;
    }

    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      for (const file of files) {
        await attachmentService.upload({ file, quoteId: id });
      }
      await loadAttachments();
    } catch (err) {
      console.error('Error uploading attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to upload attachment');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleFileInputChange = async (event) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    await uploadAttachments(Array.from(fileList));
    // Reset input so same file(s) can be selected again if needed
    event.target.value = '';
  };

  const handleAttachmentDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOverAttachment(false);
    const fileList = event.dataTransfer?.files;
    if (!fileList || fileList.length === 0) return;
    await uploadAttachments(Array.from(fileList));
  };

  const handleAttachmentDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    setIsDraggingOverAttachment(true);
  };

  const handleAttachmentDragLeave = (event) => {
    event.preventDefault();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDraggingOverAttachment(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      setAttachmentsError('');
      await attachmentService.delete(attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to delete attachment');
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      if (!attachment?.id) return;
      setAttachmentsError('');
      const res = await attachmentService.download(attachment.id);
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name || `attachment-${attachment.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to download attachment');
    }
  };

  // Prevent browser from opening files when dropped outside the drop zone (e.g. on Mac)
  useEffect(() => {
    if (activeTab !== 2) return;
    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);
    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, [activeTab]);

  const loadAddresses = async () => {
    const cid = editing && editedQuote?.customer_id != null ? editedQuote.customer_id : quote?.customer_id;
    if (!cid) return;
    try {
      setLoadingAddresses(true);
      const response = await addressService.getByCustomerId(cid);
      const list = Array.isArray(response?.data) ? response.data : response?.data?.data ?? [];
      setAddresses(list);
    } catch (err) {
      console.error('Error loading addresses:', err);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const loadContacts = async () => {
    if (!quote?.customer_id) return;
    try {
      setLoadingContacts(true);
      const response = await contactService.getByCustomerId(quote.customer_id);
      const list = Array.isArray(response?.data) ? response.data : response?.data?.data ?? [];
      setContacts(list);
    } catch (err) {
      console.error('Error loading contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  const extractRevisionMeta = (serviceResponse) => {
    const body = serviceResponse?.data ? serviceResponse.data : serviceResponse;
    const payload = body?.data || body || {};
    return {
      revisionApplied: !!payload?.revision_applied,
      newQuoteId: payload?.new_quote_id || null,
      newOrderId: payload?.new_order_id || null
    };
  };

  const navigateToRevisedQuote = ({ newQuoteId, newOrderId }) => {
    if (!newQuoteId || String(newQuoteId) === String(id)) return false;
    const params = new URLSearchParams();
    params.set('promptRegenerateOrder', '1');
    if (newOrderId) params.set('newOrderId', String(newOrderId));
    navigate(`/quotes/${newQuoteId}?${params.toString()}`);
    return true;
  };

  const saveTaxDraft = async (nextDraft) => {
    if (quoteLocked || !id || !quote || isNonLineItemQuote(quote)) return;
    const draft = nextDraft || taxDraft;
    try {
      setSaving(true);
      setError('');
      const mode = normalizeQuoteTaxModeUi(draft.mode);
      let tax_percent = null;
      let tax_fixed_amount = null;
      if (mode === 'percent') {
        const raw = String(draft.percentStr ?? '').trim();
        const t = raw === '' ? '' : formatTaxPercentDisplayValue(raw);
        if (t === '') tax_percent = null;
        else {
          const p = parseFloat(t);
          if (!Number.isFinite(p) || p < 0) {
            setError('Tax percent must be a non-negative number');
            return;
          }
          tax_percent = Math.round(p * 100) / 100;
        }
      } else if (mode === 'fixed') {
        const t = String(draft.fixedStr ?? '').trim();
        if (t === '') tax_fixed_amount = null;
        else {
          const f = parseFloat(t);
          if (!Number.isFinite(f) || f < 0) {
            setError('Tax amount must be a non-negative number');
            return;
          }
          tax_fixed_amount = Math.round(f * 100) / 100;
        }
      }
      const response = await quoteService.update(id, { tax_mode: mode, tax_percent, tax_fixed_amount });
      const revisionMeta = extractRevisionMeta(response);
      if (revisionMeta.revisionApplied && navigateToRevisedQuote(revisionMeta)) {
        return;
      }
      const serverQuote = response?.data != null ? response.data : response;
      if (serverQuote && typeof serverQuote === 'object') {
        setQuote((prev) => {
          if (!prev) return prev;
          const { lineItems: _li, relatedOrders: _ro, quoteContacts, ...rest } = serverQuote;
          return {
            ...prev,
            ...rest,
            lineItems: prev.lineItems,
            relatedOrders: prev.relatedOrders,
            quoteContacts: quoteContacts !== undefined ? quoteContacts : prev.quoteContacts
          };
        });
      }
    } catch (err) {
      console.error('Error saving quote tax:', err);
      setError(err.response?.data?.message || 'Failed to save tax');
    } finally {
      setSaving(false);
    }
  };

  const applyQuoteTaxScopePreset = async (preset) => {
    if (preset == null || quoteLocked || !id || !quote || isNonLineItemQuote(quote)) return;
    try {
      setSaving(true);
      setError('');
      let body;
      if (preset === 'line_items') {
        body = { tax_basis: 'per_line', tax_mode: 'none', tax_percent: null, tax_fixed_amount: null };
      } else if (preset === 'no_tax') {
        body = { tax_basis: 'document', tax_mode: 'none', tax_percent: null, tax_fixed_amount: null };
      } else {
        const currentMode = normalizeQuoteTaxModeUi(quote.tax_mode);
        const nextMode = currentMode === 'none' ? 'percent' : currentMode;
        body = { tax_basis: 'document', tax_mode: nextMode };
        if (nextMode === 'percent') {
          body.tax_percent =
            quote.tax_percent != null && quote.tax_percent !== '' ? Number(quote.tax_percent) : null;
          body.tax_fixed_amount = null;
        } else {
          body.tax_fixed_amount =
            quote.tax_fixed_amount != null && quote.tax_fixed_amount !== ''
              ? Number(quote.tax_fixed_amount)
              : null;
          body.tax_percent = null;
        }
      }
      const response = await quoteService.update(id, body);
      const revisionMeta = extractRevisionMeta(response);
      if (revisionMeta.revisionApplied && navigateToRevisedQuote(revisionMeta)) {
        return;
      }
      // Merge server quote (tax + totals); keep lineItems / relatedOrders — avoid loadQuote() so we don't setLoading(true).
      const serverQuote = response?.data != null ? response.data : response;
      if (serverQuote && typeof serverQuote === 'object') {
        setQuote((prev) => {
          if (!prev) return prev;
          const { lineItems: _li, relatedOrders: _ro, quoteContacts, ...rest } = serverQuote;
          return {
            ...prev,
            ...rest,
            lineItems: prev.lineItems,
            relatedOrders: prev.relatedOrders,
            quoteContacts: quoteContacts !== undefined ? quoteContacts : prev.quoteContacts
          };
        });
      }
    } catch (err) {
      console.error('Error saving quote tax scope:', err);
      setError(err.response?.data?.message || 'Failed to update tax');
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddLineItem = (itemTypeRaw) => {
    const nextType = itemTypeRaw === 'service' ? 'service' : 'part';
    setEditingLineItems(true);
    setNewLineItems([
      ...newLineItems,
      {
        part_id: nextType === 'part' ? '' : '',
        service_id: nextType === 'service' ? '' : '',
        item_type: nextType,
        quantity: 1,
        unit_price: 0,
        unit: nextType === 'service' ? 'HR' : 'EA',
        description: '',
        notes: '',
        status: 'pending',
        line_tax_mode: 'none',
        line_tax_percent: '',
        line_tax_fixed_amount: '',
        isNew: true
      }
    ]);
  };

  const handleLineItemChange = (index, field, value) => {
    if (field === 'item_ref' && value === '__new_part__') {
      setNewPartModalRowIndex(index);
      setNewPartModalItemId(null);
      setNewPartModalOpen(true);
      return;
    }
    if (field === 'item_ref' && value === '__new_service__') {
      setNewServiceModalRowIndex(index);
      setNewServiceModalItemId(null);
      setNewServiceModalOpen(true);
      return;
    }
    const updated = [...newLineItems];
    const row = { ...updated[index] };

    if (field === 'item_ref') {
      delete row.item_ref;
      if (value === '' || value == null) {
        row.part_id = '';
        row.service_id = '';
        updated[index] = row;
        setNewLineItems(updated);
        return;
      }
      const part = parts.find((p) => String(p.id) === String(value));
      const service = services.find((s) => String(s.id) === String(value));
      if (part) {
        row.item_type = 'part';
        row.part_id = String(part.id);
        row.service_id = '';
        row.description = part.part_description || part.description || '';
        row.unit_price = part.unit_price || 0;
        row.unit = part.unit || 'EA';
      } else if (service) {
        row.item_type = 'service';
        row.service_id = String(service.id);
        row.part_id = '';
        row.description = service.service_description || service.description || '';
        row.unit_price = service.unit_price || 0;
        row.unit = service.unit || 'HR';
      }
      updated[index] = row;
      setNewLineItems(updated);
      return;
    }

    const nextRow = {
      ...row,
      [field]: value
    };
    if (field === 'quantity' && nextRow.item_type === 'service') {
      const r = parseFloat(value) || 0;
      nextRow.quantity = Math.round(r * 100) / 100;
    }
    updated[index] = nextRow;
    setNewLineItems(updated);
  };

  const handleRemoveLineItem = (index) => {
    const next = newLineItems.filter((_, i) => i !== index);
    setNewLineItems(next);
    if (next.length === 0) {
      setEditingLineItems(false);
    }
  };


  const closeLineItemPopout = () => {
    setLineItemPopoutOpen(false);
    setLineItemPopoutTarget({ kind: 'new', index: null, itemId: null });
    setLineItemPopoutDraft(null);
  };

  const openLineItemPopoutForNew = (index) => {
    if (!Number.isInteger(index) || index < 0) return;
    const src = newLineItems?.[index];
    if (!src) return;
    setLineItemPopoutTarget({ kind: 'new', index, itemId: null });
    setLineItemPopoutDraft({ ...src });
    setLineItemPopoutOpen(true);
  };

  const openLineItemPopoutForExisting = (itemId, fallbackItem = null) => {
    if (!itemId) return;
    const src = editedLineItems?.[itemId] || fallbackItem || quote?.lineItems?.find((x) => x.id === itemId);
    if (!src) return;
    setLineItemPopoutTarget({ kind: 'existing', index: null, itemId });
    setLineItemPopoutDraft({ ...src });
    setLineItemPopoutOpen(true);
  };

  const saveLineItemPopout = () => {
    if (!lineItemPopoutOpen || !lineItemPopoutDraft) return;
    const draft = { ...lineItemPopoutDraft };
    if ((draft.item_type || 'part') === 'service') {
      const r = parseFloat(String(draft.quantity));
      if (Number.isFinite(r)) draft.quantity = Math.round(r * 100) / 100;
    } else {
      const n = Math.max(0, Math.trunc(Math.round(parseFloat(String(draft.quantity)) || 0)));
      draft.quantity = n;
    }
    if (lineItemPopoutTarget.kind === 'new') {
      const idx = lineItemPopoutTarget.index;
      if (!Number.isInteger(idx) || idx < 0) return;
      setNewLineItems((prev) => {
        const updated = [...prev];
        if (!updated[idx]) return updated;
        updated[idx] = { ...updated[idx], ...draft };
        return updated;
      });
      closeLineItemPopout();
      return;
    }
    if (lineItemPopoutTarget.kind === 'existing') {
      const itemId = lineItemPopoutTarget.itemId;
      if (!itemId) return;
      setEditedLineItems((prev) => ({
        ...prev,
        [itemId]: {
          ...(prev[itemId] || {}),
          ...draft
        }
      }));
      setEditingItemId(itemId);
      closeLineItemPopout();
    }
  };

  const handleLineItemPopoutRefChange = (value) => {
    const raw = String(value || '');
    const [kind, rawId] = raw.includes(':') ? raw.split(':') : [null, raw];
    const id = String(rawId || '').trim();
    if (!id) return;
    const part = kind === 'part' ? parts.find((p) => String(p.id) === id) : parts.find((p) => String(p.id) === id);
    const service = kind === 'service' ? services.find((s) => String(s.id) === id) : services.find((s) => String(s.id) === id);
    if (!part && !service) return;
    setLineItemPopoutDraft((prev) => ({
      ...(prev || {}),
      item_type: part ? 'part' : 'service',
      part_id: part ? id : '',
      service_id: service ? id : '',
      description: part
        ? (part.part_description || part.description || '')
        : (service.service_description || service.description || ''),
      unit_price: part ? (part.unit_price || 0) : (service.unit_price || 0),
      unit: part ? (part.unit || 'EA') : (service.unit || 'HR')
    }));
  };

  const handleSaveLineItems = async () => {
    try {
      setSaving(true);
      setError('');
      
      // Save all new line items
      for (const item of newLineItems) {
        const addResponse = await quoteService.addLineItem(id, { ...toLineItemPayload(item), job_number: null });
        const revisionMeta = extractRevisionMeta(addResponse);
        if (revisionMeta.revisionApplied && navigateToRevisedQuote(revisionMeta)) {
          return;
        }
      }
      
      // Reload quote to get updated line items and total
      await loadQuote();
      setNewLineItems([]);
      setEditingLineItems(false);
    } catch (err) {
      console.error('Error saving line items:', err);
      setError(err.response?.data?.message || 'Failed to save line items');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelLineItems = () => {
    setNewLineItems([]);
    setEditingLineItems(false);
  };

  const handleNewPartCreated = (part, lineItemFields) => {
    if (!part) return;
    setParts((prev) => [...prev, part]);
    const qty = lineItemFields?.quantity != null && Number(lineItemFields.quantity) > 0 ? Number(lineItemFields.quantity) : 1;
    if (newPartModalRowIndex !== null) {
      const updated = [...newLineItems];
      updated[newPartModalRowIndex] = {
        ...updated[newPartModalRowIndex],
        item_type: 'part',
        part_id: part.id,
        service_id: '',
        description: lineItemFields?.description ?? part.part_description ?? part.description ?? '',
        notes: updated[newPartModalRowIndex].notes ?? '',
        quantity: qty,
        unit_price: part.unit_price || 0,
        unit: part.unit || 'EA'
      };
      setNewLineItems(updated);
    } else if (newPartModalItemId) {
      setEditedLineItems({
        ...editedLineItems,
        [newPartModalItemId]: {
          ...editedLineItems[newPartModalItemId],
          part_id: part.id,
          description: lineItemFields?.description ?? part.part_description ?? part.description ?? '',
          notes: editedLineItems[newPartModalItemId]?.notes ?? '',
          quantity: qty,
          unit_price: part.unit_price || 0,
          unit: part.unit || 'EA'
        }
      });
    }
    setNewPartModalOpen(false);
    setNewPartModalRowIndex(null);
    setNewPartModalItemId(null);
  };

  const handleNewServiceCreated = (service, lineItemFields) => {
    if (!service) return;
    setServices((prev) => [...prev, service]);
    const qty = lineItemFields?.quantity != null && Number(lineItemFields.quantity) > 0 ? Number(lineItemFields.quantity) : 1;
    if (newServiceModalRowIndex !== null) {
      const updated = [...newLineItems];
      updated[newServiceModalRowIndex] = {
        ...updated[newServiceModalRowIndex],
        item_type: 'service',
        service_id: service.id,
        part_id: '',
        description: lineItemFields?.description ?? service.service_description ?? service.description ?? '',
        notes: updated[newServiceModalRowIndex].notes ?? '',
        quantity: qty,
        unit_price: service.unit_price || 0,
        unit: service.unit || 'HR'
      };
      setNewLineItems(updated);
    } else if (newServiceModalItemId) {
      setEditedLineItems({
        ...editedLineItems,
        [newServiceModalItemId]: {
          ...editedLineItems[newServiceModalItemId],
          item_type: 'service',
          service_id: service.id,
          part_id: '',
          description: lineItemFields?.description ?? service.service_description ?? service.description ?? '',
          notes: editedLineItems[newServiceModalItemId]?.notes ?? '',
          quantity: qty,
          unit_price: service.unit_price || 0,
          unit: service.unit || 'HR'
        }
      });
    }
    setNewServiceModalOpen(false);
    setNewServiceModalRowIndex(null);
    setNewServiceModalItemId(null);
  };

  const handleEditLineItem = (item) => {
    setEditingItemId(item.id);
    const it = item.item_type || 'part';
    const qRaw =
      item.quantity != null && String(item.quantity).trim() !== '' ? item.quantity : 1;
    const quantity =
      it === 'service'
        ? formatQuantityDisplay(Number(qRaw) || 0, { itemType: 'service' }) || String(qRaw)
        : String(Math.max(0, Math.trunc(Math.round(Number(qRaw)))));
    setEditedLineItems({
      [item.id]: {
        item_type: it,
        part_id: item.part_id || '',
        service_id: item.service_id || '',
        quantity,
        unit_price: item.unit_price != null ? item.unit_price : 0,
        unit: item.unit || (item.item_type === 'service' ? 'HR' : 'EA'),
        description: item.description || '',
        notes: item.notes || '',
        status: item.status || 'pending',
        line_tax_mode: item.line_tax_mode || 'none',
        line_tax_percent:
          item.line_tax_percent != null && item.line_tax_percent !== ''
            ? formatTaxPercentDisplayValue(item.line_tax_percent)
            : '',
        line_tax_fixed_amount:
          item.line_tax_fixed_amount != null && item.line_tax_fixed_amount !== ''
            ? String(item.line_tax_fixed_amount)
            : ''
      }
    });
  };

  const handleCancelEditLineItem = () => {
    setEditingItemId(null);
    setEditedLineItems({});
  };

  const handleApplyQuoteLineTaxPatch = (itemId, patch) => {
    if (!patch) return;
    setEditedLineItems((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), ...patch }
    }));
  };

  const handleNewQuoteLineTaxPatch = (index, patch) => {
    if (!patch) return;
    setNewLineItems((prev) => {
      const u = [...prev];
      if (!u[index]) return prev;
      u[index] = { ...u[index], ...patch };
      return u;
    });
  };

  const handleUpdateLineItemField = (itemId, field, value) => {
    if (field === 'item_ref' && value === '__new_part__') {
      setNewPartModalRowIndex(null);
      setNewPartModalItemId(itemId);
      setNewPartModalOpen(true);
      return;
    }
    if (field === 'item_ref' && value === '__new_service__') {
      setNewServiceModalRowIndex(null);
      setNewServiceModalItemId(itemId);
      setNewServiceModalOpen(true);
      return;
    }
    if (field === 'quantity') {
      setEditedLineItems((prev) => {
        const cur = prev[itemId] || {};
        const isSvc = cur.item_type === 'service';
        if (isSvc) {
          return {
            ...prev,
            [itemId]: {
              ...cur,
              quantity: value
            }
          };
        }
        const t = String(value).trim();
        if (t === '' || t === '-') {
          return { ...prev, [itemId]: { ...cur, quantity: '' } };
        }
        const n = Math.max(0, Math.trunc(Math.round(parseFloat(t))));
        return { ...prev, [itemId]: { ...cur, quantity: String(n) } };
      });
      return;
    }
    setEditedLineItems((prev) => {
      const cur = prev[itemId] || {};
      let next = { ...cur, [field]: value };
      if (field === 'item_ref' && value) {
        const part = parts.find((p) => String(p.id) === String(value));
        const service = services.find((s) => String(s.id) === String(value));
        if (part || service) {
          const item_type = part ? 'part' : 'service';
          next = {
            ...next,
            item_type,
            part_id: part ? value : '',
            service_id: service ? value : '',
            description: part
              ? part.part_description || part.description || ''
              : service.service_description || service.description || '',
            unit_price: part ? part.unit_price || 0 : service.unit_price || 0,
            unit: part ? part.unit || 'EA' : service.unit || 'HR'
          };
          if (item_type === 'part') {
            const qn = Math.max(0, Math.trunc(Math.round(parseFloat(String(next.quantity)) || 0)));
            next.quantity = String(qn);
          }
        }
      }
      return { ...prev, [itemId]: next };
    });
  };

  const handleSaveLineItem = async (itemId) => {
    try {
      setSaving(true);
      setError('');
      
      const itemData = editedLineItems[itemId];
      const baseline = quote?.lineItems?.find((li) => String(li.id) === String(itemId));
      const updateResponse = await quoteService.updateLineItem(id, itemId, {
        ...toLineItemPayload(itemData),
        job_number: baseline?.job_number ?? null
      });
      const revisionMeta = extractRevisionMeta(updateResponse);
      if (revisionMeta.revisionApplied && navigateToRevisedQuote(revisionMeta)) {
        return;
      }

      await refreshQuoteQuiet();
      setEditingItemId(null);
      setEditedLineItems({});
    } catch (err) {
      console.error('Error updating line item:', err);
      setError(err.response?.data?.message || 'Failed to update line item');
    } finally {
      setSaving(false);
    }
  };

  const enterQuoteEditMode = () => {
    if (!quote) return;
    setEditedQuote({
      customer_id: quote.customer_id ?? '',
      quote_date: quote.quote_date ? formatDateForInput(quote.quote_date) : '',
      followup_date: quote.followup_date ? formatDateForInput(quote.followup_date) : '',
      expires_on: quote.expires_on || quote.valid_until ? formatDateForInput(quote.expires_on || quote.valid_until) : '',
      description: quote.description || '',
      status: quote.status || 'draft',
      quote_style: quote.quote_style || 'standard',
      sow_scope_sections: getSowScopeSections(quote).length ? getSowScopeSections(quote) : [''],
      sow_clarifications_sections: getSowClarSections(quote).length ? getSowClarSections(quote) : [''],
      proposal_total_amount: parseFloat(quote.proposal_total_amount) || 0,
      proposal_materials_amount: parseFloat(quote.proposal_materials_amount) || 0,
      proposal_labor_amount: parseFloat(quote.proposal_labor_amount) || 0,
      proposal_rentals_amount: parseFloat(quote.proposal_rentals_amount) || 0,
      proposal_show_breakdown: !!quote.proposal_show_breakdown,
      sow_list_style: normalizeSowListStyle(quote.sow_list_style),
      shipping_customer_address_id:
        quote.shipping_customer_address_id != null ? String(quote.shipping_customer_address_id) : '',
      shipping_manual_line1: quote.shipping_manual_line1 ?? '',
      shipping_manual_line2: quote.shipping_manual_line2 ?? '',
      shipping_manual_city: quote.shipping_manual_city ?? '',
      shipping_manual_state: quote.shipping_manual_state ?? '',
      shipping_manual_zip: quote.shipping_manual_zip ?? '',
      shipping_manual_country: quote.shipping_manual_country ?? '',
      po_number: quote.po_number ?? ''
    });
    setFollowupDateManuallyEdited(false);
    setEditing(true);
  };

  const handleEditQuote = async () => {
    if (editing) {
      setEditing(false);
      return;
    }
    if (!quote) return;
    try {
      const relRes = await quoteService.getRelatedOrders(id);
      const orders = relRes?.success && Array.isArray(relRes.data?.orders) ? relRes.data.orders : [];
      setRelatedOrders(orders);
      if (orders.length > 0) {
        setQuoteEditRelatedWarningOpen(true);
        return;
      }
    } catch (e) {
      console.warn('related-orders', e);
    }
    enterQuoteEditMode();
  };

  const confirmQuoteEditDespiteRelatedOrders = () => {
    setQuoteEditRelatedWarningOpen(false);
    enterQuoteEditMode();
  };

  const handleQuoteFieldChange = (field) => (e) => {
    const value = e.target.value;
    
    if (field === 'quote_date') {
      // When quote_date changes, update followup_date if it hasn't been manually edited
      setEditedQuote(prev => {
        const newData = {
          ...prev,
          [field]: value
        };
        if (!followupDateManuallyEdited && value) {
          newData.followup_date = addDaysToDate(value, 7);
          newData.expires_on = addOneMonth(value);
        }
        return newData;
      });
    } else if (field === 'followup_date') {
      // Mark followup_date as manually edited when user changes it
      setFollowupDateManuallyEdited(true);
      setEditedQuote({
        ...editedQuote,
        [field]: value
      });
    } else {
      setEditedQuote({
        ...editedQuote,
        [field]: value
      });
    }
  };

  const handleEditedSowScopeChange = (index, value) => {
    if (!editedQuote) return;
    const sections = Array.isArray(editedQuote.sow_scope_sections) ? [...editedQuote.sow_scope_sections] : [''];
    sections[index] = value;
    setEditedQuote({ ...editedQuote, sow_scope_sections: sections });
  };
  const handleAddEditedSowScope = () => {
    if (!editedQuote) return;
    const sections = Array.isArray(editedQuote.sow_scope_sections) ? [...editedQuote.sow_scope_sections] : [];
    sections.push('');
    setEditedQuote({ ...editedQuote, sow_scope_sections: sections });
  };
  const handleRemoveEditedSowScope = (index) => {
    if (!editedQuote) return;
    const sections = Array.isArray(editedQuote.sow_scope_sections) ? [...editedQuote.sow_scope_sections] : [];
    sections.splice(index, 1);
    setEditedQuote({ ...editedQuote, sow_scope_sections: sections.length ? sections : [''] });
  };

  const handleEditedSowClarChange = (index, value) => {
    if (!editedQuote) return;
    const sections = Array.isArray(editedQuote.sow_clarifications_sections)
      ? [...editedQuote.sow_clarifications_sections]
      : [''];
    sections[index] = value;
    setEditedQuote({ ...editedQuote, sow_clarifications_sections: sections });
  };
  const handleAddEditedSowClar = () => {
    if (!editedQuote) return;
    const sections = Array.isArray(editedQuote.sow_clarifications_sections)
      ? [...editedQuote.sow_clarifications_sections]
      : [];
    sections.push('');
    setEditedQuote({ ...editedQuote, sow_clarifications_sections: sections });
  };
  const handleRemoveEditedSowClar = (index) => {
    if (!editedQuote) return;
    const sections = Array.isArray(editedQuote.sow_clarifications_sections)
      ? [...editedQuote.sow_clarifications_sections]
      : [];
    sections.splice(index, 1);
    setEditedQuote({
      ...editedQuote,
      sow_clarifications_sections: sections.length ? sections : ['']
    });
  };

  const editedQuoteShippingSelectValue =
    editedQuote && String(editedQuote.shipping_manual_line1 || '').trim() !== ''
      ? 'custom'
      : editedQuote && editedQuote.shipping_customer_address_id
        ? String(editedQuote.shipping_customer_address_id)
        : '';

  const handleEditedShippingSelect = (e) => {
    if (!editedQuote) return;
    const v = e.target.value;
    if (v === '') {
      setEditedQuote({
        ...editedQuote,
        shipping_customer_address_id: '',
        shipping_manual_line1: '',
        shipping_manual_line2: '',
        shipping_manual_city: '',
        shipping_manual_state: '',
        shipping_manual_zip: '',
        shipping_manual_country: ''
      });
    } else if (v === 'custom') {
      setEditedQuote({
        ...editedQuote,
        shipping_customer_address_id: ''
      });
    } else {
      setEditedQuote({
        ...editedQuote,
        shipping_customer_address_id: v,
        shipping_manual_line1: '',
        shipping_manual_line2: '',
        shipping_manual_city: '',
        shipping_manual_state: '',
        shipping_manual_zip: '',
        shipping_manual_country: ''
      });
    }
  };

  const handleSaveQuote = async () => {
    try {
      setSaving(true);
      setError('');

      const updateData = {
        customer_id: editedQuote.customer_id != null && editedQuote.customer_id !== '' ? parseInt(editedQuote.customer_id, 10) : quote.customer_id,
        quote_date: editedQuote.quote_date || null,
        followup_date: editedQuote.followup_date || null,
        expires_on: editedQuote.expires_on || null,
        valid_until: editedQuote.expires_on || null,
        description: editedQuote.description || null,
        status: editedQuote.status || 'draft',
        quote_style: editedQuote.quote_style || quote.quote_style || 'standard',
        field_scope_sections: [],
        field_materials_amount: 0,
        field_labor_amount: 0,
        field_rentals_amount: 0,
        field_show_breakdown: false,
        sow_scope_sections: (editedQuote.sow_scope_sections || []).map((s) => String(s || '').trim()).filter(Boolean),
        sow_clarifications_sections: (editedQuote.sow_clarifications_sections || [])
          .map((s) => String(s || '').trim())
          .filter(Boolean),
        proposal_total_amount: parseFloat(editedQuote.proposal_total_amount) || 0,
        proposal_materials_amount: parseFloat(editedQuote.proposal_materials_amount) || 0,
        proposal_labor_amount: parseFloat(editedQuote.proposal_labor_amount) || 0,
        proposal_rentals_amount: parseFloat(editedQuote.proposal_rentals_amount) || 0,
        proposal_show_breakdown: !!editedQuote.proposal_show_breakdown,
        sow_list_style: normalizeSowListStyle(editedQuote.sow_list_style),
        shipping_customer_address_id:
          editedQuote.shipping_customer_address_id !== '' && editedQuote.shipping_customer_address_id != null
            ? parseInt(editedQuote.shipping_customer_address_id, 10)
            : null,
        shipping_manual_line1: editedQuote.shipping_manual_line1,
        shipping_manual_line2: editedQuote.shipping_manual_line2,
        shipping_manual_city: editedQuote.shipping_manual_city,
        shipping_manual_state: editedQuote.shipping_manual_state,
        shipping_manual_zip: editedQuote.shipping_manual_zip,
        shipping_manual_country: editedQuote.shipping_manual_country,
        po_number: editedQuote.po_number != null && String(editedQuote.po_number).trim() !== ''
          ? String(editedQuote.po_number).trim()
          : null
      };

      const response = await quoteService.update(id, updateData);
      const revisionMeta = extractRevisionMeta(response);
      if (revisionMeta.revisionApplied && navigateToRevisedQuote(revisionMeta)) {
        return;
      }
      await loadQuote();
      setEditing(false);
      setEditedQuote(null);
    } catch (err) {
      console.error('Error updating quote:', err);
      setError(err.response?.data?.message || 'Failed to update quote');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEditQuote = () => {
    setEditing(false);
    setEditedQuote(null);
    setFollowupDateManuallyEdited(false);
  };

  const handleDeleteQuote = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDeleteQuote = async () => {
    try {
      setError('');
      await quoteService.delete(id);
      navigate('/quotes');
    } catch (err) {
      console.error('Error deleting quote:', err);
      setError(err.response?.data?.message || 'Failed to delete quote');
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteLineItem = (itemId) => {
    setLineItemToDelete(itemId);
    setDeleteLineItemDialogOpen(true);
  };

  const confirmDeleteLineItem = async () => {
    if (!lineItemToDelete) return;
    
    try {
      const deleteResponse = await quoteService.deleteLineItem(id, lineItemToDelete);
      const revisionMeta = extractRevisionMeta(deleteResponse);
      if (revisionMeta.revisionApplied && navigateToRevisedQuote(revisionMeta)) {
        return;
      }
      await loadQuote();
      setDeleteLineItemDialogOpen(false);
      setLineItemToDelete(null);
    } catch (err) {
      console.error('Error deleting line item:', err);
      setError(err.response?.data?.message || 'Failed to delete line item');
      setDeleteLineItemDialogOpen(false);
      setLineItemToDelete(null);
    }
  };

  const handleConvertToOrder = () => {
    setConvertDialogOpen(true);
  };

  const handlePrint = () => {
    printWithFilename(quotePrintFilename);
  };

  const handlePrintFromQuoteSettingsDialog = () => {
    const { hideNotes, hideDescription, separateLineSections } = quotePrintSettingsDraft;
    setPrintLayoutHideNotes(hideNotes);
    setPrintLayoutHideDescription(hideDescription);
    setPrintLayoutSeparateLineSections(!!separateLineSections);
    setQuotePrintSettingsOpen(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        printWithFilename(quotePrintFilename);
      });
    });
  };

  const handleDownloadProposalPdf = async () => {
    try {
      setError('');
      const blob = await quoteService.downloadProposalPdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quote-${quote?.identifier || id}-proposal.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Proposal PDF download failed:', err);
      setError(err.response?.data?.message || err.message || 'Failed to download proposal PDF');
    }
  };

  const confirmConvertToOrder = async () => {
    try {
      setSaving(true);
      setError('');
      setConvertDialogOpen(false);
      const response = await quoteService.convertToOrder(id);
      
      if (response.success) {
        // Navigate to the new order
        navigate(`/work-orders/${response.data.id}`);
      } else {
        setError(response.message || 'Failed to convert quote');
      }
    } catch (err) {
      console.error('Error converting quote:', err);
      setError(err.response?.data?.message || 'Failed to convert quote');
    } finally {
      setSaving(false);
    }
  };

  const closeRegenerateOrderPrompt = () => {
    setRegenerateOrderPromptOpen(false);
    setSuggestedRevisedOrderId(null);
    const params = new URLSearchParams(searchParams);
    params.delete('promptRegenerateOrder');
    params.delete('newOrderId');
    navigate(`/quotes/${id}${params.toString() ? `?${params.toString()}` : ''}`, { replace: true });
  };

  const confirmRegenerateOrderFromPrompt = async () => {
    try {
      setSaving(true);
      setError('');
      if (suggestedRevisedOrderId) {
        const response = await orderService.reviseOrder(suggestedRevisedOrderId);
        const payload = response?.data ?? {};
        const newOrderId = payload?.newOrder?.id ?? payload?.new_order_id;
        if (response?.success && newOrderId) {
          setRegenerateOrderPromptOpen(false);
          setSuggestedRevisedOrderId(null);
          navigate(`/work-orders/${newOrderId}`);
          return;
        }
        setError(response?.message || 'Failed to regenerate order');
        return;
      }
      const response = await quoteService.convertToOrder(id);
      if (response.success && response.data?.id) {
        setRegenerateOrderPromptOpen(false);
        setSuggestedRevisedOrderId(null);
        navigate(`/work-orders/${response.data.id}`);
        return;
      }
      setError(response?.message || 'Failed to convert quote');
    } catch (err) {
      console.error('Error regenerating order from revised quote:', err);
      setError(err.response?.data?.message || 'Failed to regenerate order');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      open: 'info',
      won: 'success',
      lost: 'error',
      expired: 'warning',
      deprecated: 'warning'
    };
    return colors[status] || 'default';
  };

  const hasRelatedOrders = (relatedOrders?.length ?? 0) > 0;
  const isDeprecatedQuote = quote?.status === 'deprecated';
  const quoteLocked = isDeprecatedQuote;

  const persistPrintedVersionOptions = async (payload) => {
    if (quoteLocked || saving || !id || !quote) return;
    try {
      setSaving(true);
      setError('');
      const response = await quoteService.update(id, payload);
      const revisionMeta = extractRevisionMeta(response);
      if (revisionMeta.revisionApplied && navigateToRevisedQuote(revisionMeta)) {
        return;
      }
      await loadQuote();
      if (editing && editedQuote) {
        setEditedQuote((prev) =>
          prev
            ? {
                ...prev,
                ...(payload.sow_list_style != null
                  ? { sow_list_style: normalizeSowListStyle(payload.sow_list_style) }
                  : {}),
                ...(payload.proposal_show_breakdown !== undefined
                  ? { proposal_show_breakdown: !!payload.proposal_show_breakdown }
                  : {})
              }
            : prev
        );
      }
    } catch (err) {
      console.error('Error updating printed version options:', err);
      setError(err.response?.data?.message || 'Failed to update quote');
    } finally {
      setSaving(false);
    }
  };

  const handleSowListStyleChange = (e) => {
    const next = normalizeSowListStyle(e.target.value);
    if (editing && editedQuote) {
      setEditedQuote({ ...editedQuote, sow_list_style: next });
      return;
    }
    void persistPrintedVersionOptions({ sow_list_style: next });
  };

  const handleProposalShowBreakdownChange = (e) => {
    const checked = e.target.checked;
    if (editing && editedQuote) {
      setEditedQuote({ ...editedQuote, proposal_show_breakdown: checked });
      return;
    }
    void persistPrintedVersionOptions({ proposal_show_breakdown: checked });
  };

  const formatModifiedTimestamp = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '—';
    // Example target: "26/03/26 2pm"
    return format(d, 'dd/MM/yy h:mma').replace(':00', '').toLowerCase();
  };
  const existingPartItems = (quote?.lineItems || []).filter((item) => item.item_type !== 'service');
  const existingServiceItems = (quote?.lineItems || []).filter((item) => item.item_type === 'service');
  const referenceQuoteIdentifier = getQuoteReferenceIdentifier(quote);
  const newPartItems = newLineItems
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.item_type !== 'service');
  const newServiceItems = newLineItems
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.item_type === 'service');

  const computeQuoteLineSubtotal = (row) => {
    const q = Number(row?.quantity) || 0;
    const p = Number(row?.unit_price) || 0;
    return Math.round(q * p * 100) / 100;
  };

  let quoteLineItemsSubtotalTotal = 0;
  let quoteLineItemsTaxSum = 0;
  const quoteTaxBasisUi = normalizeTaxBasisUi(quote?.tax_basis);
  const isPerLineQuoteTax = quoteTaxBasisUi === 'per_line' && !isNonLineItemQuote(quote);
  const quoteLineItemsTaxEditActive =
    editingLineItems || editingItemId != null || newLineItems.length > 0;
  const quoteTaxScopeToggleValue = isPerLineQuoteTax
    ? 'line_items'
    : normalizeQuoteTaxModeUi(quote?.tax_mode) === 'none'
      ? 'no_tax'
      : 'order';
  for (const item of quote?.lineItems || []) {
    const row =
      String(editingItemId) === String(item.id) && editedLineItems[item.id]
        ? editedLineItems[item.id]
        : item;
    const sub = computeQuoteLineSubtotal(row);
    quoteLineItemsSubtotalTotal += sub;
    quoteLineItemsTaxSum += computeLineItemTaxAmount(sub, row);
  }
  for (const item of newLineItems) {
    const sub = computeQuoteLineSubtotal(item);
    quoteLineItemsSubtotalTotal += sub;
    quoteLineItemsTaxSum += computeLineItemTaxAmount(sub, item);
  }

  const displayTaxAmount = !isNonLineItemQuote(quote)
    ? isPerLineQuoteTax
      ? Math.round(quoteLineItemsTaxSum * 100) / 100
      : computeDisplayedQuoteTax(
          quoteLineItemsSubtotalTotal,
          taxDraft.mode,
          taxDraft.percentStr,
          taxDraft.fixedStr
        )
    : 0;
  const displayGrandTotal = !isNonLineItemQuote(quote)
    ? Math.round((quoteLineItemsSubtotalTotal + displayTaxAmount) * 100) / 100
    : Number(quote?.proposal_total_amount ?? quote?.total_amount ?? 0) || 0;

  const quoteLineSubtotalColumnAmount = (row) => {
    const sub = computeQuoteLineSubtotal(row);
    return isPerLineQuoteTax ? computeLineTotalWithPerLineTax(sub, row) : sub;
  };

  // Shared column sizing rules for Quote Detail line-item tables (headers + body).
  // Keep text-heavy columns flexible/wider and numeric columns compact with safe minimums.
  const lineItemColumnSizing = {
    '& th:nth-of-type(1), & td:nth-of-type(1)': { width: '5% !important', minWidth: 64, textAlign: 'center' },
    '& th:nth-of-type(2), & td:nth-of-type(2)': { width: '8% !important', minWidth: 88, textAlign: 'center' },
    '& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20% !important', minWidth: 240 },
    '& th:nth-of-type(4), & td:nth-of-type(4)': { width: '7% !important', minWidth: 88, textAlign: 'center' },
    '& th:nth-of-type(5), & td:nth-of-type(5)': { width: '7% !important', minWidth: 80, textAlign: 'center' },
    '& th:nth-of-type(6), & td:nth-of-type(6)': { width: '9% !important', minWidth: 100, textAlign: 'right' },
    '& th:nth-of-type(7), & td:nth-of-type(7)': { width: '5.5% !important', minWidth: 88, textAlign: 'right' },
    '& th:nth-of-type(8), & td:nth-of-type(8)': { width: '11% !important', minWidth: 128, textAlign: 'right' },
    '& th:nth-of-type(9), & td:nth-of-type(9)': { width: '18% !important', minWidth: 220 },
    '& th:nth-of-type(10), & td:nth-of-type(10)': { width: '9% !important', minWidth: 120 },
    '& th:nth-of-type(11), & td:nth-of-type(11)': { width: '9% !important', minWidth: 120 }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !quote) {
    return (
      <Box>
        <Alert severity="error">{error || 'Quote not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/quotes')} sx={{ mt: 2 }}>
          Back to Quotes
        </Button>
      </Box>
    );
  }

  const printOrderedQuoteLineItems =
    quote.lineItems?.length > 0
      ? [
          ...quote.lineItems.filter((item) => item.item_type !== 'service'),
          ...quote.lineItems.filter((item) => item.item_type === 'service')
        ]
      : [];

  const quotePrintPartLines = printOrderedQuoteLineItems.filter((i) => i.item_type !== 'service');
  const quotePrintServiceLines = printOrderedQuoteLineItems.filter((i) => i.item_type === 'service');
  const quotePrintPartsSubtotal = quotePrintPartLines.reduce((s, i) => s + quotePrintLineExtension(i), 0);
  const quotePrintServicesSubtotal = quotePrintServiceLines.reduce((s, i) => s + quotePrintLineExtension(i), 0);
  const quotePrintSubtotalAll = printOrderedQuoteLineItems.reduce((acc, item) => acc + quotePrintLineExtension(item), 0);
  const quotePrintLineTableNumCols = isPerLineQuoteTax ? 8 : 7;
  const quotePrintLabelColSpan = quotePrintLineTableNumCols - 1;
  const quotePrintTaxColTh = {
    border: 'none',
    padding: '4px 4px',
    fontSize: '11px',
    textAlign: 'right',
    backgroundColor: '#f0f0f0',
    width: '52px',
    maxWidth: '56px',
    whiteSpace: 'nowrap'
  };
  const quotePrintTaxColTd = {
    border: 'none',
    padding: '4px 4px',
    fontSize: '11px',
    textAlign: 'right',
    verticalAlign: 'top'
  };

  const renderQuotePrintLineTableHead = () => (
    <thead>
      <tr>
        <th style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'left', backgroundColor: '#f0f0f0', width: '40px' }}>Item</th>
        <th style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'left', backgroundColor: '#f0f0f0', width: '70px' }}>Type</th>
        <th style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'left', backgroundColor: '#f0f0f0', width: '80px' }}>Code</th>
        <th style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'left', backgroundColor: '#f0f0f0' }}>Description</th>
        <th style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0', width: '80px' }}>Quantity</th>
        <th style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0', width: '100px' }}>Price</th>
        {isPerLineQuoteTax ? <th style={quotePrintTaxColTh}>Tax</th> : null}
        <th style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0', width: '110px' }}>Line Total</th>
      </tr>
    </thead>
  );

  const renderQuotePrintLineRow = (item, displayIndex) => {
    const qty = item.quantity ?? 0;
    const unitPrice = item.unit_price ?? 0;
    const lineSubtotal = quotePrintLineExtension(item);
    const isService = item.item_type === 'service';
    const unit = item.unit || (isService ? 'HR' : 'EA');
    const code = isService ? effectiveServiceCode(item) || '—' : item.part_number || '—';
    const baseDesc = item.description || item.part_description || item.service_description || '—';
    const partDesc = baseDesc + (!printLayoutHideNotes && item.notes ? `\n${item.notes}` : '');
    const lineTaxAmt = isPerLineQuoteTax ? computeLineItemTaxAmount(lineSubtotal, item) : 0;
    const lineTaxPrint =
      isPerLineQuoteTax && Math.round(lineTaxAmt * 100) / 100 > 0 ? formatLineItemTaxDollars(lineTaxAmt) : '';
    return (
      <tr key={item.id ?? `qpl-${displayIndex}`}>
        <td style={{ border: 'none', padding: '4px 6px', fontSize: '11px', verticalAlign: 'top' }}>{displayIndex + 1}</td>
        <td style={{ border: 'none', padding: '4px 6px', fontSize: '11px', verticalAlign: 'top' }}>{isService ? 'Service' : 'Part'}</td>
        <td style={{ border: 'none', padding: '4px 6px', fontSize: '11px', verticalAlign: 'top' }}>{code}</td>
        <td style={{ border: 'none', padding: '4px 6px', fontSize: '11px', whiteSpace: 'pre-wrap', verticalAlign: 'top' }}>{partDesc}</td>
        <td style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', verticalAlign: 'top' }}>
          {formatQuantityDisplay(qty ?? 0, { itemType: isService ? 'service' : 'part' })}
        </td>
        <td style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', verticalAlign: 'top' }}>{formatCurrency(unitPrice)} /{unit}</td>
        {isPerLineQuoteTax ? <td style={quotePrintTaxColTd}>{lineTaxPrint}</td> : null}
        <td style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', verticalAlign: 'top' }}>
          {formatCurrency(
            isPerLineQuoteTax ? computeLineTotalWithPerLineTax(lineSubtotal, item) : lineSubtotal
          )}
        </td>
      </tr>
    );
  };

  const renderQuotePrintSectionSubtotalFoot = (label, amount) => (
    <tfoot>
      <tr>
        <td colSpan={quotePrintLabelColSpan} style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', fontWeight: 600 }}>{label}</td>
        <td style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(amount)}</td>
      </tr>
    </tfoot>
  );

  const renderQuotePrintTotalsRows = (printSub) => {
    const printTax = isPerLineQuoteTax
      ? Math.round(
          printOrderedQuoteLineItems.reduce((s, i) => {
            const sub = quotePrintLineExtension(i);
            return s + computeLineItemTaxAmount(sub, i);
          }, 0) * 100
        ) / 100
      : computeDisplayedQuoteTax(
          printSub,
          quote.tax_mode,
          quote.tax_percent != null && quote.tax_percent !== '' ? String(quote.tax_percent) : '',
          quote.tax_fixed_amount != null && quote.tax_fixed_amount !== '' ? String(quote.tax_fixed_amount) : ''
        );
    const printGrand = Math.round((Number(printSub) + Number(printTax)) * 100) / 100;
    return (
      <>
        <tr>
          <td colSpan={quotePrintLabelColSpan} style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', fontWeight: 600 }}>
            Line items total
          </td>
          <td style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(printSub)}</td>
        </tr>
        <tr>
          <td colSpan={quotePrintLabelColSpan} style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', fontWeight: 600 }}>
            Tax
          </td>
          <td style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(printTax)}</td>
        </tr>
        <tr>
          <td colSpan={quotePrintLabelColSpan} style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', fontWeight: 700 }}>
            Total (incl. tax)
          </td>
          <td style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(printGrand)}</td>
        </tr>
      </>
    );
  };

  return (
    <>
      {/* Print-friendly view - only visible when printing */}
      <PrintPage
        title="Quotation"
        showSpacer={false}
        footer={
          <div
            className="print-footer print-quote-footer"
            style={{ paddingTop: '10px', borderTop: '1px solid #ccc', fontSize: '11px', color: '#333', textAlign: 'center' }}
          >
            <div style={{ whiteSpace: 'pre-wrap', marginBottom: '12px' }}>{dependentClause}</div>
            <div style={{ fontSize: '10px', color: '#666' }}>Generated on {format(new Date(), 'MM/dd/yyyy hh:mm a')}</div>
          </div>
        }
      >
        {/* Recipient: left column "Quote" / "To" (bold), right column customer name & address (not bold) */}
        <div className="print-quote-recipient">
          <div className="print-quote-to-labels">
            <strong>Quote</strong>
            <strong>To</strong>
          </div>
          <div className="print-quote-to-address">
            <div>{quote.company_name || `${quote.customer_first_name || ''} ${quote.customer_last_name || ''}`.trim() || 'Customer'}</div>
            {quoteBillingToAddressLines(quote).map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        </div>

        {/* Quote details box - two columns */}
        <div className="print-quote-details-box">
          <div className="print-quote-details-left">
            <div><strong>Quote Identifier:</strong> {formatQuoteNumber(quote)}</div>
            <div><strong>Quote Date:</strong> {formatDateDisplay(quote.quote_date) || 'N/A'} &nbsp; <strong>Expires:</strong> {formatDateDisplay(quote.expires_on || quote.valid_until) || '—'}</div>
            <div><strong>Customer:</strong> {quote.company_name || `${quote.customer_first_name || ''} ${quote.customer_last_name || ''}`.trim() || '—'}</div>
            <div><strong>Ship Via:</strong> {quote.ship_via || '—'}</div>
          </div>
          <div className="print-quote-details-right">
            <div><strong>Contact:</strong> {quote.primary_contact_name || (quote.quoteContacts && quote.quoteContacts[0] ? (quote.quoteContacts[0].name || `${quote.quoteContacts[0].first_name || ''} ${quote.quoteContacts[0].last_name || ''}`.trim() || '—') : '—')}</div>
            <div><strong>Terms:</strong> {quote.payment_terms || 'Payable On Receipt'}</div>
            <div><strong>Phone:</strong> {quote.customer_phone || '—'}</div>
          </div>
        </div>

        {String(quote.notes ?? '').trim() && !printLayoutHideNotes ? (
          <div
            style={{
              width: '100%',
              marginBottom: '1em',
              paddingTop: 10,
              borderTop: '1px solid #ccc',
              textAlign: 'left'
            }}
          >
            <strong>Notes:</strong>
            <div style={{ fontWeight: 'normal', whiteSpace: 'pre-wrap', marginTop: 4 }}>{String(quote.notes).trim()}</div>
          </div>
        ) : null}

        <div style={{ marginBottom: '2em' }} />

        {isScopeOfWorkQuote(quote) ? (
          <>
            {quote.description && !printLayoutHideDescription ? (
              <div className="print-section" style={{ marginBottom: '16px', marginTop: 0 }}>
                <strong>Description:</strong> {quote.description}
              </div>
            ) : null}
            <div className="print-sow-block" style={{ marginBottom: 12 }}>
              <div className="print-sow-heading">Field Work / Service</div>
              {getSowScopeSections(quote).length > 0 ? (
                renderPrintSowItemList(getSowScopeSections(quote), quote.sow_list_style, 'print-sow-scope')
              ) : (
                <div style={{ color: '#666' }}>No scope provided.</div>
              )}
            </div>
            {getSowClarSections(quote).length > 0 && (
              <div className="print-sow-block" style={{ marginBottom: 12 }}>
                <div className="print-sow-heading">Clarifications and Exceptions</div>
                {renderPrintSowItemList(getSowClarSections(quote), quote.sow_list_style, 'print-sow-clar')}
              </div>
            )}
            {!!quote.proposal_show_breakdown && (
              <table className="print-table print-quote-line-table print-table-no-zebra" style={{ border: 'none', marginBottom: 8 }}>
                <tbody>
                  <tr>
                    <td style={{ border: 'none', padding: '6px 8px', fontWeight: 600 }}>Materials</td>
                    <td style={{ border: 'none', padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(quote.proposal_materials_amount || 0)}</td>
                  </tr>
                  <tr>
                    <td style={{ border: 'none', padding: '6px 8px', fontWeight: 600 }}>Labor</td>
                    <td style={{ border: 'none', padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(quote.proposal_labor_amount || 0)}</td>
                  </tr>
                  <tr>
                    <td style={{ border: 'none', padding: '6px 8px', fontWeight: 600 }}>Rentals</td>
                    <td style={{ border: 'none', padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(quote.proposal_rentals_amount || 0)}</td>
                  </tr>
                </tbody>
              </table>
            )}
            <div className="print-quote-total-row">
              <strong>Total proposal amount:</strong>
              <span>{formatCurrency(quote.proposal_total_amount ?? quote.total_amount ?? 0)}</span>
            </div>
          </>
        ) : (
          <>
            {quote.description && !printLayoutHideDescription ? (
              <div className="print-section" style={{ marginBottom: '20px', marginTop: '10px' }}>
                <strong>Description:</strong> {quote.description}
              </div>
            ) : null}

            {!printLayoutSeparateLineSections ? (
              <table className="print-table print-quote-line-table" style={{ border: 'none' }}>
                {renderQuotePrintLineTableHead()}
                <tbody>
                  {printOrderedQuoteLineItems.length > 0 ? (
                    printOrderedQuoteLineItems.map((item, index) => renderQuotePrintLineRow(item, index))
                  ) : (
                    <tr>
                      <td colSpan={quotePrintLineTableNumCols} style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'center' }}>
                        No line items
                      </td>
                    </tr>
                  )}
                  {printOrderedQuoteLineItems.length > 0 ? renderQuotePrintTotalsRows(quotePrintSubtotalAll) : null}
                </tbody>
              </table>
            ) : (
              <div className="print-order-line-sections-wrap" style={{ marginBottom: '20px' }}>
                {printOrderedQuoteLineItems.length > 0 ? (
                  <>
                    {quotePrintPartLines.length > 0 ? (
                      <div className="print-order-line-section">
                        <div className="print-order-line-section-title">Parts and Materials</div>
                        <table className="print-table print-quote-line-table" style={{ border: 'none', width: '100%' }}>
                          {renderQuotePrintLineTableHead()}
                          <tbody>
                            {quotePrintPartLines.map((item, index) => renderQuotePrintLineRow(item, index))}
                          </tbody>
                          {renderQuotePrintSectionSubtotalFoot('Parts and Materials total', quotePrintPartsSubtotal)}
                        </table>
                      </div>
                    ) : null}
                    {quotePrintServiceLines.length > 0 ? (
                      <div
                        className={`print-order-line-section${quotePrintPartLines.length > 0 ? ' print-order-line-section--labor' : ''}`}
                      >
                        <div className="print-order-line-section-title">Labor and Services</div>
                        <table className="print-table print-quote-line-table" style={{ border: 'none', width: '100%' }}>
                          {renderQuotePrintLineTableHead()}
                          <tbody>
                            {quotePrintServiceLines.map((item, index) =>
                              renderQuotePrintLineRow(item, quotePrintPartLines.length + index)
                            )}
                          </tbody>
                          {renderQuotePrintSectionSubtotalFoot('Labor and Services total', quotePrintServicesSubtotal)}
                        </table>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <>
                    <div className="print-order-line-section">
                      <div className="print-order-line-section-title">Parts and Materials</div>
                      <table className="print-table print-quote-line-table" style={{ border: 'none', width: '100%' }}>
                        {renderQuotePrintLineTableHead()}
                        <tbody>
                          <tr>
                            <td colSpan={quotePrintLineTableNumCols} style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'center' }}>
                              No line items
                            </td>
                          </tr>
                        </tbody>
                        {renderQuotePrintSectionSubtotalFoot('Parts and Materials total', 0)}
                      </table>
                    </div>
                    <div className="print-order-line-section print-order-line-section--labor">
                      <div className="print-order-line-section-title">Labor and Services</div>
                      <table className="print-table print-quote-line-table" style={{ border: 'none', width: '100%' }}>
                        {renderQuotePrintLineTableHead()}
                        <tbody>
                          <tr>
                            <td colSpan={quotePrintLineTableNumCols} style={{ border: 'none', padding: '4px 6px', fontSize: '11px', textAlign: 'center' }}>
                              No line items
                            </td>
                          </tr>
                        </tbody>
                        {renderQuotePrintSectionSubtotalFoot('Labor and Services total', 0)}
                      </table>
                    </div>
                  </>
                )}
                {printOrderedQuoteLineItems.length > 0 ? (
                  <table className="print-table print-quote-line-table" style={{ border: 'none', width: '100%', marginTop: 8 }}>
                    <tbody>{renderQuotePrintTotalsRows(quotePrintSubtotalAll)}</tbody>
                  </table>
                ) : null}
              </div>
            )}

            {printOrderedQuoteLineItems.length === 0 ? (
              <div className="print-quote-total-row">
                <strong>Total:</strong>
                <span>{formatCurrency(quote.total_amount || 0)}</span>
              </div>
            ) : null}
          </>
        )}
      </PrintPage>

      {/* Regular view */}
      <Box className="no-print">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/quotes')} size="small" sx={{ mb: 0.5 }}>
            Back
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              Quotation: {formatQuoteNumber(quote)}
            </Typography>
            {revisionDialogItems.length > 0 && (
              <Tooltip title="View previous revisions">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setRevisionDialogOpen(true)}
                  aria-label="View previous quote revisions"
                  sx={{ mt: 0.25 }}
                >
                  <TextSnippet fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Box>
          {editing ? (
            <>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<Save />}
                onClick={handleSaveQuote}
                disabled={saving}
                sx={{ mr: 0.5 }}
              >
                Save
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancelEditQuote}
                disabled={saving}
                sx={{ mr: 0.5 }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <IconButton size="small" color="primary" title="Edit" onClick={handleEditQuote} disabled={quoteLocked}>
                <Edit fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" title="Delete" onClick={handleDeleteQuote} disabled={quoteLocked}>
                <Delete fontSize="small" />
              </IconButton>
              <IconButton size="small" color="primary" title="Copy">
                <ContentCopy fontSize="small" />
              </IconButton>
              <Tooltip title="Print settings">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    setQuotePrintSettingsDraft({
                      hideNotes: printLayoutHideNotes,
                      hideDescription: printLayoutHideDescription,
                      separateLineSections: printLayoutSeparateLineSections
                    });
                    setQuotePrintSettingsOpen(true);
                  }}
                  aria-label="Print settings"
                >
                  <Settings fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" color="primary" title="Print" onClick={handlePrint}>
                <Print fontSize="small" />
              </IconButton>
              {isScopeOfWorkQuote(quote) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDownloadProposalPdf}
                  sx={{ ml: 0.5, textTransform: 'none' }}
                >
                  Proposal PDF
                </Button>
              )}
              {!quoteLocked && quote.status === 'open' && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ ml: 0.5, textTransform: 'none' }}
                  onClick={handleConvertToOrder}
                >
                  Create Order
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Quote summary bar (same style as Order / Customer pages) */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', alignContent: 'flex-start' }}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Quote #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatQuoteNumber(quote)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
              Reference Quote
            </Typography>
            <Typography variant="body2">
              {referenceQuoteIdentifier ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={openReferenceQuote}
                >
                  {referenceQuoteIdentifier}
                </Button>
              ) : (
                '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Quote Date</Typography>
            <Typography variant="body2">{quote.quote_date ? formatDateDisplay(quote.quote_date) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer PO</Typography>
            <Typography variant="body2">{quote.po_number || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Orders</Typography>
            <Typography variant="body2" component="div" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
              {relatedOrders.length === 0 ? (
                '—'
              ) : (
                <>
                  {relatedOrders.slice(0, 3).map((ord) => (
                    <Button
                      key={ord.id}
                      size="small"
                      variant="outlined"
                      onClick={() => navigate(`/work-orders/${ord.id}`)}
                      sx={{ textTransform: 'none', py: 0.25, px: 0.75, minWidth: 0 }}
                    >
                      {ord.identifier ?? ord.work_order_number ?? ord.id}
                    </Button>
                  ))}
                  {relatedOrders.length > 3 && (
                    <Button size="small" onClick={() => setActiveTab(3)} sx={{ textTransform: 'none' }}>
                      +{relatedOrders.length - 3} more
                    </Button>
                  )}
                </>
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer</Typography>
            <Typography variant="body2">
              {quote.customer_id != null ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/customers/${quote.customer_id}`)}
                >
                  {(quote.company_name ?? [quote.customer_first_name, quote.customer_last_name].filter(Boolean).join(' ')) || quote.customer_number || `#${quote.customer_id}`}
                </Button>
              ) : (
                (quote.company_name ?? [quote.customer_first_name, quote.customer_last_name].filter(Boolean).join(' ')) || '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Quote Total</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatCurrency(!isNonLineItemQuote(quote) ? displayGrandTotal : quote.total_amount, '—')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Quoted By</Typography>
            <Typography variant="body2">
              {quote.quoted_by_first_name && quote.quoted_by_last_name
                ? `${quote.quoted_by_first_name} ${quote.quoted_by_last_name}`
                : quote.quoted_by ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Status</Typography>
            <Chip label={quote.status || 'draft'} size="small" sx={{ textTransform: 'capitalize' }} />
          </Box>
          {quote.followup_date && (
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Followup</Typography>
              <Typography variant="body2">{formatDateDisplay(quote.followup_date)}</Typography>
            </Box>
          )}
          {quote.expires_on && (
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Expires</Typography>
              <Typography variant="body2">{formatDateDisplay(quote.expires_on)}</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 1.5 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="General" />
          <Tab label="Contacts" />
          <Tab label={`Attachments ${attachments.length || 0}`} />
          <Tab label={`Order History ${relatedOrders.length || 0}`} />
          <Tab label="Lineage" />
          <Tab label="FOOTER" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {/* Only show "no customer" when we have no customer_id AND no customer data from API (defensive: list/detail both return customer_number when quote has customer) */}
          {hasRelatedOrders && !isDeprecatedQuote ? (
            <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
              {relatedOrders.slice(0, 5).map((ord) => (
                <Button
                  key={ord.id}
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(`/work-orders/${ord.id}`)}
                >
                  {ord.identifier ?? ord.work_order_number ?? ord.id}
                </Button>
              ))}
              {relatedOrders.length > 5 && (
                <Button size="small" variant="text" onClick={() => setActiveTab(3)}>
                  View all ({relatedOrders.length})
                </Button>
              )}
            </Box>
          ) : null}
          {isDeprecatedQuote && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              This Quote was modified {formatModifiedTimestamp(quote.quote_last_edited_at)} by{' '}
              {[quote.quote_last_edited_by_first_name, quote.quote_last_edited_by_last_name].filter(Boolean).join(' ') || '—'}. The next version of this Quote is{' '}
              {quote?.superseded_by_quote_id ? (
                <Button
                  color="inherit"
                  size="small"
                  variant="text"
                  onClick={() => navigate(`/quotes/${quote.superseded_by_quote_id}`)}
                  sx={{ textTransform: 'none', ml: 0.75, px: 0.75 }}
                >
                  {quote.superseded_by_quote_identifier ?? quote.superseded_by_quote_number ?? quote.superseded_by_quote_id}
                </Button>
              ) : (
                '—'
              )}
            </Alert>
          )}
          {quote.customer_id == null && quote.customer_number == null && quote.company_name == null && (quote.customer_first_name == null && quote.customer_last_name == null) && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              This quote has no customer. Every quotation should have a customer.
            </Alert>
          )}
          <Card sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 1.5 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Quote Identifier"
                    value={formatQuoteNumber(quote) || ''}
                    InputProps={{ readOnly: true }}
                    margin="dense"
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Quote Description"
                    value={editing && editedQuote ? editedQuote.description ?? '' : quote.description ?? ''}
                    onChange={handleQuoteFieldChange('description')}
                    InputProps={{ readOnly: !editing }}
                    margin="dense"
                    size="small"
                    multiline
                    minRows={4}
                    maxRows={4}
                    placeholder="Brief description of this quote"
                    sx={{
                      '& .MuiInputBase-inputMultiline': {
                        overflowY: 'auto',
                        resize: 'none',
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Customer PO"
                    value={editing && editedQuote ? editedQuote.po_number ?? '' : quote.po_number ?? ''}
                    onChange={handleQuoteFieldChange('po_number')}
                    InputProps={{ readOnly: !editing }}
                    margin="dense"
                    size="small"
                    placeholder="Customer's purchase order reference"
                  />
                  <TextField
                    fullWidth
                    label="Reference Quote"
                    value={referenceQuoteIdentifier || '—'}
                    InputProps={{ readOnly: true }}
                    margin="dense"
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Quote Notes"
                    value={editing && editedQuote ? editedQuote.notes ?? '' : quote.notes ?? ''}
                    onChange={handleQuoteFieldChange('notes')}
                    InputProps={{ readOnly: !editing }}
                    margin="dense"
                    size="small"
                    multiline
                    minRows={3}
                    maxRows={4}
                  />
                  {editing ? (
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel>Customer</InputLabel>
                      <Select
                        value={editedQuote?.customer_id ?? quote.customer_id ?? ''}
                        onChange={(e) => {
                          const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                          setEditedQuote({
                            ...editedQuote,
                            customer_id: v,
                            shipping_customer_address_id: '',
                            shipping_manual_line1: '',
                            shipping_manual_line2: '',
                            shipping_manual_city: '',
                            shipping_manual_state: '',
                            shipping_manual_zip: '',
                            shipping_manual_country: ''
                          });
                        }}
                        label="Customer"
                        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                      >
                        <MenuItem value="">
                          <em>Select customer</em>
                        </MenuItem>
                        {customers.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.customer_number ? `${c.customer_number} – ` : ''}
                            {c.company_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || `Customer #${c.id}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        label="Customer Code"
                        value={quote.customer_number ?? customerDetail?.customer_number ?? ''}
                        InputProps={{ readOnly: true }}
                        margin="dense"
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Customer Name"
                        value={
                          quote.company_name ??
                          (quote.customer_first_name || quote.customer_last_name
                            ? `${quote.customer_first_name || ''} ${quote.customer_last_name || ''}`.trim()
                            : null) ??
                          (customerDetail?.company_name ?? (customerDetail?.first_name || customerDetail?.last_name ? `${customerDetail?.first_name || ''} ${customerDetail?.last_name || ''}`.trim() : null)) ??
                          (quote.customer_id ? 'Customer not found (may have been deleted)' : '')
                        }
                        InputProps={{ readOnly: true }}
                        margin="dense"
                        size="small"
                      />
                    </>
                  )}
                  {quote.customer_id != null && !editing && (
                    <Box sx={{ mt: 1.5 }}>
                      <Button
                        component={Link}
                        to={`/customers/${quote.customer_id}`}
                        variant="outlined"
                        size="small"
                        color="primary"
                      >
                        View / Edit Customer
                      </Button>
                    </Box>
                  )}
                  <TextField
                    fullWidth
                    label="Quote Total"
                    value={formatCurrency(!isNonLineItemQuote(quote) ? displayGrandTotal : quote.total_amount, '')}
                    InputProps={{ readOnly: true }}
                    margin="dense"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Quote Date"
                    type="date"
                    value={editing && editedQuote ? editedQuote.quote_date : formatDateForInput(quote.quote_date)}
                    onChange={handleQuoteFieldChange('quote_date')}
                    InputProps={{ readOnly: !editing }}
                    margin="dense"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiInputBase-input': { minWidth: 180 } }}
                  />
                  <TextField
                    fullWidth
                    label="Followup Date"
                    type="date"
                    value={editing && editedQuote ? editedQuote.followup_date : formatDateForInput(quote.followup_date)}
                    onChange={handleQuoteFieldChange('followup_date')}
                    InputProps={{ readOnly: !editing }}
                    margin="dense"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiInputBase-input': { minWidth: 180 } }}
                  />
                  <TextField
                    fullWidth
                    label="Expires On"
                    type="date"
                    value={editing && editedQuote ? editedQuote.expires_on : formatDateForInput(quote.expires_on || quote.valid_until)}
                    onChange={handleQuoteFieldChange('expires_on')}
                    InputProps={{ readOnly: !editing }}
                    margin="dense"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiInputBase-input': { minWidth: 180 } }}
                  />
                  {editing ? (
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={editedQuote?.status || quote.status || 'draft'}
                        onChange={(e) => setEditedQuote({ ...editedQuote, status: e.target.value })}
                        label="Status"
                      >
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="open">Open</MenuItem>
                        <MenuItem value="won">Won</MenuItem>
                        <MenuItem value="lost">Lost</MenuItem>
                        <MenuItem value="expired">Expired</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={quote.status || 'draft'}
                        color={getStatusColor(quote.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>
                  )}
                  {editing ? (
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel>Quote Style</InputLabel>
                      <Select
                        value={editedQuote?.quote_style || quote.quote_style || 'standard'}
                        onChange={(e) => setEditedQuote({ ...editedQuote, quote_style: e.target.value })}
                        label="Quote Style"
                      >
                        <MenuItem value="standard">Standard</MenuItem>
                        <MenuItem value="scope_of_work">Field Work / Service</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      label="Quote Style"
                      value={isScopeOfWorkQuote(quote) ? 'Field Work / Service' : 'Standard'}
                      InputProps={{ readOnly: true }}
                      margin="dense"
                      size="small"
                    />
                  )}
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>Shipping Address</Typography>
                  {editing && editedQuote ? (
                    <>
                      <FormControl fullWidth margin="dense" size="small" sx={{ mt: 0.5 }}>
                        <InputLabel>Quote shipping address</InputLabel>
                        <Select
                          value={editedQuoteShippingSelectValue}
                          label="Quote shipping address"
                          onChange={handleEditedShippingSelect}
                        >
                          <MenuItem value="">Customer default (profile)</MenuItem>
                          {addresses.map((addr) => (
                            <MenuItem key={addr.id} value={String(addr.id)}>
                              {[
                                (addr.address_type || '').toLowerCase() === 'shipping' ? '[Ship] ' : '',
                                addr.address_line1,
                                [addr.city, addr.state].filter(Boolean).join(', ')
                              ].filter(Boolean).join(' ') || `Address #${addr.id}`}
                            </MenuItem>
                          ))}
                          <MenuItem value="custom">Other (enter manually)</MenuItem>
                        </Select>
                      </FormControl>
                      {editedQuoteShippingSelectValue === 'custom' && (
                        <Box sx={{ mt: 0.5 }}>
                          <TextField
                            fullWidth
                            size="small"
                            margin="dense"
                            label="Address line 1"
                            value={editedQuote.shipping_manual_line1}
                            onChange={(e) =>
                              setEditedQuote({
                                ...editedQuote,
                                shipping_manual_line1: e.target.value,
                                shipping_customer_address_id: ''
                              })
                            }
                          />
                          <TextField
                            fullWidth
                            size="small"
                            margin="dense"
                            label="Address line 2"
                            value={editedQuote.shipping_manual_line2}
                            onChange={(e) =>
                              setEditedQuote({ ...editedQuote, shipping_manual_line2: e.target.value })
                            }
                          />
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                fullWidth
                                size="small"
                                margin="dense"
                                label="City"
                                value={editedQuote.shipping_manual_city}
                                onChange={(e) =>
                                  setEditedQuote({ ...editedQuote, shipping_manual_city: e.target.value })
                                }
                              />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                              <TextField
                                fullWidth
                                size="small"
                                margin="dense"
                                label="State"
                                value={editedQuote.shipping_manual_state}
                                onChange={(e) =>
                                  setEditedQuote({ ...editedQuote, shipping_manual_state: e.target.value })
                                }
                              />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                              <TextField
                                fullWidth
                                size="small"
                                margin="dense"
                                label="ZIP"
                                value={editedQuote.shipping_manual_zip}
                                onChange={(e) =>
                                  setEditedQuote({ ...editedQuote, shipping_manual_zip: e.target.value })
                                }
                              />
                            </Grid>
                          </Grid>
                          <TextField
                            fullWidth
                            size="small"
                            margin="dense"
                            label="Country"
                            value={editedQuote.shipping_manual_country}
                            onChange={(e) =>
                              setEditedQuote({ ...editedQuote, shipping_manual_country: e.target.value })
                            }
                          />
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {(() => {
                        if (loadingAddresses) return 'Loading…';
                        if (quote.shipping_address_line1) {
                          return [
                            quote.shipping_address_line1,
                            quote.shipping_address_line2,
                            [quote.shipping_city, quote.shipping_state, quote.shipping_zip].filter(Boolean).join(', '),
                            quote.shipping_country && quote.shipping_country !== 'United States' ? quote.shipping_country : ''
                          ].filter(Boolean).join('\n') || '—';
                        }
                        const shipping = addresses.find(a => (a.address_type || '').toLowerCase() === 'shipping') || addresses.find(a => a.is_default) || addresses[0];
                        if (!shipping) return '—';
                        return [
                          shipping.address_line1,
                          shipping.address_line2,
                          [shipping.city, shipping.state, shipping.zip_code].filter(Boolean).join(', '),
                          shipping.country && shipping.country !== 'United States' ? shipping.country : ''
                        ].filter(Boolean).join('\n') || '—';
                      })()}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {showFieldWorkServiceFields && (
            <Card sx={{ mb: 1.5 }}>
              <CardContent sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1 }}>
                  Field Work / Service
                </Typography>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                  Scope of work
                </Typography>
                {(editing ? editedQuote?.sow_scope_sections || [''] : getSowScopeSections(quote).length ? getSowScopeSections(quote) : ['']).map(
                  (section, index) => (
                    <Box key={`sow-scope-${index}`} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={index === 0 ? 'Scope' : `Scope ${index + 1}`}
                        value={section}
                        InputProps={{ readOnly: !editing }}
                        onChange={(e) => handleEditedSowScopeChange(index, e.target.value)}
                        size="small"
                      />
                      {editing && (
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveEditedSowScope(index)}
                          disabled={(editedQuote?.sow_scope_sections || []).length <= 1}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  )
                )}
                {editing && (
                  <Button variant="outlined" size="small" onClick={handleAddEditedSowScope} sx={{ mb: 2 }}>
                    Add scope section
                  </Button>
                )}
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Clarifications & exclusions
                </Typography>
                {(editing
                  ? editedQuote?.sow_clarifications_sections || ['']
                  : getSowClarSections(quote).length
                    ? getSowClarSections(quote)
                    : ['']
                ).map((section, index) => (
                  <Box key={`sow-clar-${index}`} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label={index === 0 ? 'Clarification' : `Clarification ${index + 1}`}
                      value={section}
                      InputProps={{ readOnly: !editing }}
                      onChange={(e) => handleEditedSowClarChange(index, e.target.value)}
                      size="small"
                    />
                    {editing && (
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveEditedSowClar(index)}
                        disabled={(editedQuote?.sow_clarifications_sections || []).length <= 1}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {editing && (
                  <Button variant="outlined" size="small" onClick={handleAddEditedSowClar} sx={{ mb: 1.5 }}>
                    Add clarification section
                  </Button>
                )}
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <CurrencyTextField
                      fullWidth
                      size="small"
                      label="Proposal total amount"
                      dollarAdornment
                      minimumFractionDigits={2}
                      maximumFractionDigits={2}
                      value={editing ? editedQuote?.proposal_total_amount ?? '' : quote.proposal_total_amount ?? ''}
                      onChange={(e) =>
                        setEditedQuote({ ...editedQuote, proposal_total_amount: e.target.value })
                      }
                      InputProps={{ readOnly: !editing }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CurrencyTextField
                      fullWidth
                      size="small"
                      label="Materials"
                      dollarAdornment
                      minimumFractionDigits={2}
                      maximumFractionDigits={2}
                      value={editing ? editedQuote?.proposal_materials_amount ?? '' : quote.proposal_materials_amount ?? ''}
                      onChange={(e) =>
                        setEditedQuote({ ...editedQuote, proposal_materials_amount: e.target.value })
                      }
                      InputProps={{ readOnly: !editing }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CurrencyTextField
                      fullWidth
                      size="small"
                      label="Labor"
                      dollarAdornment
                      minimumFractionDigits={2}
                      maximumFractionDigits={2}
                      value={editing ? editedQuote?.proposal_labor_amount ?? '' : quote.proposal_labor_amount ?? ''}
                      onChange={(e) =>
                        setEditedQuote({ ...editedQuote, proposal_labor_amount: e.target.value })
                      }
                      InputProps={{ readOnly: !editing }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CurrencyTextField
                      fullWidth
                      size="small"
                      label="Rentals"
                      dollarAdornment
                      minimumFractionDigits={2}
                      maximumFractionDigits={2}
                      value={editing ? editedQuote?.proposal_rentals_amount ?? '' : quote.proposal_rentals_amount ?? ''}
                      onChange={(e) =>
                        setEditedQuote({ ...editedQuote, proposal_rentals_amount: e.target.value })
                      }
                      InputProps={{ readOnly: !editing }}
                    />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    mt: 1,
                    ...(quoteLocked && { pointerEvents: 'none', opacity: 0.65, cursor: 'not-allowed' }),
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
                    Printed Version Options:
                  </Typography>
                  <Box
                    sx={{
                      width: '66.666%',
                      maxWidth: '100%',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'stretch',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        flex: { xs: 'none', sm: '1 1 0' },
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: 0,
                        boxSizing: 'border-box',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1.5,
                        py: 1,
                        bgcolor: 'action.hover',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 600,
                          color: 'text.secondary',
                          letterSpacing: 0.2,
                          textTransform: 'uppercase',
                          mb: 0.75,
                        }}
                      >
                        Price breakdown
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        Materials, labor, and rentals lines on the printed quote
                      </Typography>
                      <FormControlLabel
                        sx={{
                          m: 0,
                          alignItems: 'center',
                        }}
                        control={
                          <Checkbox
                            checked={editing ? !!editedQuote?.proposal_show_breakdown : !!quote.proposal_show_breakdown}
                            onChange={handleProposalShowBreakdownChange}
                            disabled={quoteLocked || saving}
                          />
                        }
                        label="Show price breakdown on Print Version"
                      />
                    </Box>
                    <Divider
                      flexItem
                      orientation="vertical"
                      sx={{
                        display: { xs: 'none', sm: 'block' },
                      }}
                    />
                    <Box
                      sx={{
                        flex: { xs: 'none', sm: '1 1 0' },
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: 0,
                        boxSizing: 'border-box',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1.5,
                        py: 1,
                        bgcolor: 'action.hover',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        id="quote-detail-sow-list-caption"
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 600,
                          color: 'text.secondary',
                          letterSpacing: 0.2,
                          textTransform: 'uppercase',
                          mb: 0.75,
                        }}
                      >
                        List format (choose one)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        How scope and clarifications appear on the printed quote
                      </Typography>
                      <RadioGroup
                        row
                        aria-labelledby="quote-detail-sow-list-caption"
                        name="sow_list_style"
                        sx={{ flexWrap: 'wrap', gap: 0.5 }}
                        value={
                          editing
                            ? normalizeSowListStyle(editedQuote?.sow_list_style)
                            : normalizeSowListStyle(quote.sow_list_style)
                        }
                        onChange={handleSowListStyleChange}
                      >
                        <FormControlLabel
                          value="numbered"
                          sx={{ mr: 1.5 }}
                          control={<Radio size="small" disabled={quoteLocked || saving} />}
                          label="Numbered items"
                        />
                        <FormControlLabel
                          value="bullets"
                          sx={{ mr: 1.5 }}
                          control={<Radio size="small" disabled={quoteLocked || saving} />}
                          label="Bullet points"
                        />
                        <FormControlLabel
                          value="none"
                          control={<Radio size="small" disabled={quoteLocked || saving} />}
                          label="None"
                        />
                      </RadioGroup>
                    </Box>
                  </Box>
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  Proposal total:{' '}
                  {formatCurrency(
                    editing
                      ? parseFloat(editedQuote?.proposal_total_amount) || 0
                      : parseFloat(quote.proposal_total_amount) || parseFloat(quote.total_amount) || 0
                  )}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Line Items */}
          {!isNonLineItemQuote(quote) && (
          <Card>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Button 
                    startIcon={<AddIcon />} 
                    variant="contained" 
                    color="success" 
                    size="small" 
                    onClick={() => handleAddLineItem('part')}
                    disabled={saving || quoteLocked}
                  >
                    + Add Part
                  </Button>
                  {editingLineItems && (
                    <>
                      <Button 
                        startIcon={<Save />} 
                        variant="contained" 
                        color="primary" 
                        size="small" 
                        onClick={handleSaveLineItems}
                        disabled={saving || quoteLocked}
                      >
                        Save
                      </Button>
                      <Button 
                        startIcon={<Cancel />} 
                        variant="outlined" 
                        size="small" 
                        onClick={handleCancelLineItems}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    borderCollapse: 'collapse',
                    border: '1px solid #e0e0e0',
                    ...lineItemColumnSizing,
                    '& .MuiTableCell-head': { verticalAlign: 'middle' },
                    '& .MuiTextField-root .MuiInputBase-root': { fontSize: '0.8125rem' },
                    '& .MuiTextField-root .MuiOutlinedInput-input': {
                      fontSize: '0.8125rem',
                      lineHeight: 1.35,
                      padding: '4px 8px'
                    },
                    '& td:nth-of-type(4) .MuiOutlinedInput-input, & td:nth-of-type(5) .MuiOutlinedInput-input': {
                      textAlign: 'center'
                    },
                    '& td:nth-of-type(6) .MuiOutlinedInput-input, & td:nth-of-type(7) .MuiOutlinedInput-input, & td:nth-of-type(8) .MuiOutlinedInput-input, & td:nth-of-type(9) .MuiOutlinedInput-input': {
                      textAlign: 'right'
                    },
                    '& .MuiInputBase-input': { py: 0.5 },
                    '& .MuiFormControl-root': {
                      width: '100%',
                      minWidth: 0,
                      '& .MuiInputLabel-root': { fontSize: '0.75rem', lineHeight: 1.35 }
                    },
                    '& td:nth-of-type(7) .MuiFormControl-root': { minWidth: 72 },
                    '& td:nth-of-type(8) .MuiFormControl-root': { minWidth: 112 },
                    '& .MuiTextField-root': { width: '100%' },
                    '& .MuiFormControl-root .MuiOutlinedInput-root .MuiSelect-select': {
                      fontSize: '0.8125rem',
                      lineHeight: 1.35,
                      fontWeight: 400,
                      padding: '4px 8px',
                      display: 'flex',
                      alignItems: 'center'
                    },
                    '& tbody tr.wm-line-totals-footer > td:nth-of-type(1), & tbody tr.wm-line-totals-footer > td:nth-of-type(2)': {
                      textAlign: 'right'
                    },
                    '& tbody tr.wm-line-totals-footer--subtotal > td:nth-of-type(1)': { textAlign: 'left' },
                    '& .MuiChip-root': { height: 22, fontSize: '0.7rem' },
                    '& .MuiIconButton-root': { p: 0.35 }
                  }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={11} sx={{ border: '1px solid #e0e0e0', bgcolor: '#50a8ee', fontWeight: 700 }}>
                        Parts and Materials
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ width: 18, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.5, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Item</TableCell>
                      <TableCell sx={{ width: 60, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Code</TableCell>
                      <TableCell sx={{ width: 120, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Part</TableCell>
                      <TableCell sx={{ width: 40, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Qty</TableCell>
                      <TableCell sx={{ width: 44, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Unit</TableCell>
                      <TableCell sx={{ width: 70, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Price</TableCell>
                      <TableCell
                        sx={{
                          width: 88,
                          minWidth: 88,
                          whiteSpace: 'nowrap',
                          border: '1px solid #ccc',
                          px: 0.35,
                          py: 0.5,
                          fontSize: '0.7rem',
                          bgcolor: '#1e98e95c',
                          fontWeight: 600,
                          textAlign: 'right'
                        }}
                      >
                        Tax %
                      </TableCell>
                      <TableCell sx={{ width: 128, minWidth: 128, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600, textAlign: 'right' }}>Tax $</TableCell>
                      <TableCell sx={{ width: 76, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600, textAlign: 'right' }}>
                        {isPerLineQuoteTax ? 'Line total' : 'Subtotal'}
                      </TableCell>
                      <TableCell sx={{ width: 220, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Notes</TableCell>
                      <TableCell sx={{ width: 64, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>

                    {existingPartItems.map((item, index) => {
                        const isEditing = String(editingItemId) === String(item.id);
                        const editedItem = editedLineItems[item.id] || item;
                        const partNumber = isEditing
                          ? (parts.find((p) => String(p.id) === String(editedItem.part_id))?.part_number ?? item.part_number ?? '')
                          : (item.part_number ?? '');
                        
                        return (
                          <TableRow key={item.id}>
                            <TableCell sx={{ width: 18, border: '1px solid #e0e0e0', px: 0.5, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25, minHeight: 26, display: 'flex', alignItems: 'center', fontSize: '0.8125rem' }}>{index + 1}</Box>
                            </TableCell>
                            <TableCell sx={{ width: 60, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25, minHeight: 26, display: 'flex', alignItems: 'center', fontSize: '0.8125rem' }}>{partNumber}</Box>
                            </TableCell>
                            <TableCell sx={{ width: 120, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                              {isEditing ? (
                                <FormControl fullWidth size="small">
                                  <InputLabel id={`quote-line-ps-label-${item.id}`} shrink>Part</InputLabel>
                                  <Select
                                    labelId={`quote-line-ps-label-${item.id}`}
                                    displayEmpty
                                    value={editedItem.part_id || ''}
                                    label="Part"
                                    onChange={(e) => handleUpdateLineItemField(item.id, 'item_ref', e.target.value)}
                                    renderValue={(value) => {
                                      if (!value || value === '') return '';
                                      const part = parts.find((p) => String(p.id) === String(value));
                                      return part ? formatPartDescription(part) : '';
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } }, MenuListProps: { dense: true } }}
                                  >
                                    <MenuItem value="" sx={{ py: 0.5, fontSize: '0.9rem' }}>None</MenuItem>
                                    <MenuItem value="__new_part__" sx={{ py: 0.5, fontSize: '0.9rem' }}>New Part</MenuItem>
                                    {parts.map((part) => (
                                      <MenuItem key={part.id} value={part.id} sx={{ py: 0.5, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                                        {formatPartOptionLabel(part)}
                                      </MenuItem>
                                    ))}
                                    {services.map((service) => (
                                      <MenuItem key={`svc-${service.id}`} value={service.id} sx={{ py: 0.5, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                                        {formatServiceOptionLabel(service)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                item.part_name ?? item.description ?? ''
                              )}
                            </TableCell>
                            <TableCell sx={{ width: 40, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                              {isEditing ? (
                                <TextField
                                  type="number"
                                  size="small"
                                  value={formatLineItemQtyEditValue('part', editedItem.quantity)}
                                  onChange={(e) => handleUpdateLineItemField(item.id, 'quantity', e.target.value)}
                                  inputProps={{ min: 0, step: 1 }}
                                />
                              ) : (
                                formatQuantityDisplay(item.quantity ?? 0, { itemType: 'part' })
                              )}
                            </TableCell>
                            <TableCell sx={{ width: 44, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  value={editedItem.unit || (editedItem.item_type === 'service' ? 'HR' : 'EA')}
                                  onChange={(e) => handleUpdateLineItemField(item.id, 'unit', e.target.value)}
                                />
                              ) : (
                                item.unit || (item.item_type === 'service' ? 'HR' : 'EA')
                              )}
                            </TableCell>
                            <TableCell sx={{ width: 70, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                              {isEditing ? (
                                <TextField
                                  type="number"
                                  size="small"
                                  value={editedItem.unit_price != null ? editedItem.unit_price : ''}
                                  onChange={(e) => handleUpdateLineItemField(item.id, 'unit_price', e.target.value)}
                                  inputProps={{ min: 0, step: 0.01 }}
                                />
                              ) : (
                                formatCurrency(item.unit_price != null ? item.unit_price : 0)
                              )}
                            </TableCell>
                            {renderQuoteLineTaxCells({
                              isPerLineQuoteTax,
                              row: isEditing ? editedItem : item,
                              isEditing,
                              disabled: quoteLocked,
                              onLineTaxPatch: (patch) => handleApplyQuoteLineTaxPatch(item.id, patch),
                              computeSubtotal: computeQuoteLineSubtotal
                            })}
                            <TableCell sx={{ width: 76, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'right' }}>
                              {formatCurrency(quoteLineSubtotalColumnAmount(isEditing ? editedItem : item), '$0.00')}
                            </TableCell>
                            <TableCell sx={{ width: 220, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                              {isEditing ? (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: '100%' }}>
                                  <TextField
                                    size="small"
                                    value={editedItem.notes || ''}
                                    onChange={(e) => handleUpdateLineItemField(item.id, 'notes', e.target.value)}
                                    sx={{ flex: 1, minWidth: 0 }}
                                  />
                                </Box>
                              ) : (
                                item.notes || ''
                              )}
                            </TableCell>
                            <TableCell sx={{ width: 64, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                              {isEditing ? (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <IconButton 
                                    size="small" 
                                    color="primary" 
                                    onClick={() => handleSaveLineItem(item.id)}
                                    disabled={saving || quoteLocked}
                                    title="Save"
                                  >
                                    <Save fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    onClick={handleCancelEditLineItem}
                                    disabled={saving}
                                    title="Cancel"
                                  >
                                    <Cancel fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => openLineItemPopoutForExisting(item.id, editedItem)}
                                    title="Pop out full line item"
                                    aria-label="Pop out full line item"
                                  >
                                    <OpenInNew fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    color="primary" 
                                    onClick={() => handleEditLineItem(item)}
                                    disabled={quoteLocked}
                                    title="Edit"
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    color="error" 
                                    onClick={() => handleDeleteLineItem(item.id)}
                                    disabled={quoteLocked}
                                    title="Delete"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {newPartItems.map(({ item, index }) => {
                      const newCode = item.part_id ? (parts.find((p) => String(p.id) === String(item.part_id))?.part_number ?? '') : '';
                      return (
                        <TableRow key={`new-${index}`}>
                          <TableCell sx={{ width: 18, border: '1px solid #e0e0e0', px: 0.5, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25, minHeight: 26, display: 'flex', alignItems: 'center', fontSize: '0.8125rem' }}>{(quote.lineItems?.length || 0) + index + 1}</Box>
                          </TableCell>
                          <TableCell sx={{ width: 60, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25, minHeight: 26, display: 'flex', alignItems: 'center', fontSize: '0.8125rem' }}>{newCode}</Box>
                          </TableCell>
                          <TableCell sx={{ width: 120, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                              <FormControl fullWidth size="small">
                                <InputLabel id={`quote-new-ps-label-${index}`} shrink>Part</InputLabel>
                                <Select
                                  labelId={`quote-new-ps-label-${index}`}
                                  displayEmpty
                                  value={String(item.part_id ?? '')}
                                  label="Part"
                                  onChange={(e) => handleLineItemChange(index, 'item_ref', e.target.value)}
                                  renderValue={(value) => {
                                    if (!value || value === '') return '';
                                    if (value === '__new_part__') return 'New Part';
                                    const part = parts.find((p) => String(p.id) === String(value));
                                    return part ? formatPartDescription(part) : '';
                                  }}
                                  MenuProps={{ PaperProps: { sx: { maxHeight: 320 } }, MenuListProps: { dense: true } }}
                                >
                                  <MenuItem value="" sx={{ py: 0.5, fontSize: '0.9rem' }}>None</MenuItem>
                                  <MenuItem value="__new_part__" sx={{ py: 0.5, fontSize: '0.9rem' }}>New Part</MenuItem>
                                  {parts.map((part) => (
                                    <MenuItem
                                      key={part.id}
                                      value={String(part.id)}
                                      sx={{ py: 0.5, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
                                    >
                                      {formatPartOptionLabel(part)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                          </TableCell>
                          <TableCell sx={{ width: 44, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                              inputProps={{ min: 0, step: 1 }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 48, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <TextField
                              size="small"
                              value={item.unit || (item.item_type === 'service' ? 'HR' : 'EA')}
                              onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 80, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.unit_price}
                              onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          {renderQuoteLineTaxCells({
                            isPerLineQuoteTax,
                            row: item,
                            isEditing: true,
                            disabled: quoteLocked,
                            onLineTaxPatch: (patch) => handleNewQuoteLineTaxPatch(index, patch),
                            computeSubtotal: computeQuoteLineSubtotal
                          })}
                          <TableCell sx={{ width: 76, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'right' }}>
                            {formatCurrency(quoteLineSubtotalColumnAmount(item), '$0.00')}
                          </TableCell>
                          <TableCell sx={{ width: 220, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: '100%' }}>
                              <TextField
                                size="small"
                                value={item.notes || ''}
                                onChange={(e) => handleLineItemChange(index, 'notes', e.target.value)}
                                sx={{ flex: 1, minWidth: 0 }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: 80, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openLineItemPopoutForNew(index)}
                              title="Pop out full line item"
                              aria-label="Pop out full line item"
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleRemoveLineItem(index)}
                              disabled={quoteLocked}
                              title="Remove"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell colSpan={11} sx={{ border: 0, height: 14, bgcolor: '#ffffff', p: 0 }} />
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={11} sx={{ border: 0, p: 0.5, bgcolor: 'transparent' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            startIcon={<AddIcon />}
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handleAddLineItem('service')}
                            disabled={saving || quoteLocked}
                          >
                            + Add Service
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={11} sx={{ border: '1px solid #e0e0e0', borderTop: '8px solid #ffffff', bgcolor: '#50a8ee', fontWeight: 700 }}>
                        Labor and Services
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ width: 18, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.5, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Item</TableCell>
                      <TableCell sx={{ width: 60, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Code</TableCell>
                      <TableCell sx={{ width: 120, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Service</TableCell>
                      <TableCell sx={{ width: 40, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Qty</TableCell>
                      <TableCell sx={{ width: 44, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Unit</TableCell>
                      <TableCell sx={{ width: 70, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Price</TableCell>
                      <TableCell
                        sx={{
                          width: 88,
                          minWidth: 88,
                          whiteSpace: 'nowrap',
                          border: '1px solid #ccc',
                          px: 0.35,
                          py: 0.5,
                          fontSize: '0.7rem',
                          bgcolor: '#1e98e95c',
                          fontWeight: 600,
                          textAlign: 'right'
                        }}
                      >
                        Tax %
                      </TableCell>
                      <TableCell sx={{ width: 128, minWidth: 128, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600, textAlign: 'right' }}>Tax $</TableCell>
                      <TableCell sx={{ width: 76, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600, textAlign: 'right' }}>
                        {isPerLineQuoteTax ? 'Line total' : 'Subtotal'}
                      </TableCell>
                      <TableCell sx={{ width: 220, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Notes</TableCell>
                      <TableCell sx={{ width: 64, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#1e98e95c', fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>

                    {existingServiceItems.map((item, index) => {
                      const isEditing = String(editingItemId) === String(item.id);
                      const editedItem = editedLineItems[item.id] || item;
                      const svcForCode = services.find((s) => String(s.id) === String((isEditing ? editedItem : item).service_id));
                      const code = effectiveServiceCode(
                        svcForCode || { id: item.service_id, service_code: item.service_code }
                      );
                      return (
                        <TableRow key={`svc-${item.id}`}>
                          <TableCell sx={{ width: 18, border: '1px solid #e0e0e0', px: 0.5, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25, minHeight: 26, display: 'flex', alignItems: 'center', fontSize: '0.8125rem' }}>{existingPartItems.length + index + 1}</Box>
                          </TableCell>
                          <TableCell sx={{ width: 60, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25, minHeight: 26, display: 'flex', alignItems: 'center', fontSize: '0.8125rem' }}>{code}</Box>
                          </TableCell>
                          <TableCell sx={{ width: 120, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            {isEditing ? (
                              <FormControl fullWidth size="small">
                                <InputLabel id={`quote-line-svc-label-${item.id}`} shrink>Service</InputLabel>
                                <Select
                                  labelId={`quote-line-svc-label-${item.id}`}
                                  displayEmpty
                                  value={editedItem.service_id || ''}
                                  label="Service"
                                  onChange={(e) => handleUpdateLineItemField(item.id, 'item_ref', e.target.value)}
                                  renderValue={(value) => {
                                    if (!value || value === '') return '';
                                    if (value === '__new_service__') return 'New Service';
                                    const service = services.find((s) => String(s.id) === String(value));
                                    return service ? formatServiceOptionLabel(service) : '';
                                  }}
                                  MenuProps={{ PaperProps: { sx: { maxHeight: 320 } }, MenuListProps: { dense: true } }}
                                >
                                  <MenuItem value="" sx={{ py: 0.5, fontSize: '0.9rem' }}>None</MenuItem>
                                  <MenuItem value="__new_service__" sx={{ py: 0.5, fontSize: '0.9rem' }}>New Service</MenuItem>
                                  {services.map((service) => (
                                    <MenuItem key={`svc-${service.id}`} value={service.id} sx={{ py: 0.5, fontSize: '0.9rem' }}>
                                      {formatServiceOptionLabel(service)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              item.service_name ?? item.description ?? ''
                            )}
                          </TableCell>
                          <TableCell sx={{ width: 40, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>{isEditing ? <TextField type="text" inputMode="decimal" size="small" value={formatLineItemQtyEditValue('service', editedItem.quantity)} onChange={(e) => handleUpdateLineItemField(item.id, 'quantity', e.target.value)} /> : formatQuantityDisplay(item.quantity ?? 0, { itemType: 'service' })}</TableCell>
                          <TableCell sx={{ width: 44, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>{isEditing ? <TextField size="small" value={editedItem.unit || 'HR'} onChange={(e) => handleUpdateLineItemField(item.id, 'unit', e.target.value)} /> : (item.unit || 'HR')}</TableCell>
                          <TableCell sx={{ width: 70, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>{isEditing ? <TextField type="number" size="small" value={editedItem.unit_price != null ? editedItem.unit_price : ''} onChange={(e) => handleUpdateLineItemField(item.id, 'unit_price', e.target.value)} inputProps={{ min: 0, step: 0.01 }} /> : formatCurrency(item.unit_price != null ? item.unit_price : 0)}</TableCell>
                          {renderQuoteLineTaxCells({
                            isPerLineQuoteTax,
                            row: isEditing ? editedItem : item,
                            isEditing,
                            disabled: quoteLocked,
                            onLineTaxPatch: (patch) => handleApplyQuoteLineTaxPatch(item.id, patch),
                            computeSubtotal: computeQuoteLineSubtotal
                          })}
                          <TableCell sx={{ width: 76, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'right' }}>{formatCurrency(quoteLineSubtotalColumnAmount(isEditing ? editedItem : item), '$0.00')}</TableCell>
                          <TableCell sx={{ width: 220, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>{isEditing ? <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: '100%' }}><TextField size="small" value={editedItem.notes || ''} onChange={(e) => handleUpdateLineItemField(item.id, 'notes', e.target.value)} sx={{ flex: 1, minWidth: 0 }} /></Box> : (item.notes || '')}</TableCell>
                          <TableCell sx={{ width: 64, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>{isEditing ? <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}><IconButton size="small" color="primary" onClick={() => handleSaveLineItem(item.id)} disabled={saving || quoteLocked}><Save fontSize="small" /></IconButton><IconButton size="small" onClick={handleCancelEditLineItem} disabled={saving}><Cancel fontSize="small" /></IconButton></Box> : <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}><IconButton size="small" color="primary" onClick={() => openLineItemPopoutForExisting(item.id, editedItem)} title="Pop out full line item"><OpenInNew fontSize="small" /></IconButton><IconButton size="small" color="primary" onClick={() => handleEditLineItem(item)} disabled={quoteLocked}><Edit fontSize="small" /></IconButton><IconButton size="small" color="error" onClick={() => handleDeleteLineItem(item.id)} disabled={quoteLocked}><Delete fontSize="small" /></IconButton></Box>}</TableCell>
                        </TableRow>
                      );
                    })}

                    {newServiceItems.map(({ item, index }) => {
                      const newCode = item.service_id
                        ? effectiveServiceCode(services.find((s) => String(s.id) === String(item.service_id)) || { id: item.service_id })
                        : '';
                      return (
                        <TableRow key={`new-svc-${index}`}>
                          <TableCell sx={{ width: 18, border: '1px solid #e0e0e0', px: 0.5, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25, minHeight: 26, display: 'flex', alignItems: 'center', fontSize: '0.8125rem' }}>{(quote.lineItems?.length || 0) + index + 1}</Box>
                          </TableCell>
                          <TableCell sx={{ width: 60, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25, minHeight: 26, display: 'flex', alignItems: 'center', fontSize: '0.8125rem' }}>{newCode}</Box>
                          </TableCell>
                          <TableCell sx={{ width: 120, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <FormControl fullWidth size="small">
                              <InputLabel id={`quote-new-svc-label-${index}`} shrink>Service</InputLabel>
                              <Select
                                labelId={`quote-new-svc-label-${index}`}
                                displayEmpty
                                value={String(item.service_id ?? '')}
                                label="Service"
                                onChange={(e) => handleLineItemChange(index, 'item_ref', e.target.value)}
                                renderValue={(value) => {
                                  if (!value || value === '') return '';
                                  if (value === '__new_service__') return 'New Service';
                                  const service = services.find((s) => String(s.id) === String(value));
                                  return service ? formatServiceOptionLabel(service) : '';
                                }}
                              >
                                <MenuItem value="" sx={{ py: 0.5, fontSize: '0.9rem' }}>None</MenuItem>
                                <MenuItem value="__new_service__" sx={{ py: 0.5, fontSize: '0.9rem' }}>New Service</MenuItem>
                                {services.map((service) => (
                                  <MenuItem key={`new-svc-${service.id}`} value={String(service.id)} sx={{ py: 0.5, fontSize: '0.9rem' }}>
                                    {formatServiceOptionLabel(service)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell sx={{ width: 44, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}><TextField type="number" size="small" value={item.quantity} onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)} inputProps={{ min: 0, step: 'any' }} /></TableCell>
                          <TableCell sx={{ width: 48, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}><TextField size="small" value={item.unit || 'HR'} onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)} /></TableCell>
                          <TableCell sx={{ width: 80, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}><TextField type="number" size="small" value={item.unit_price} onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)} inputProps={{ min: 0, step: 0.01 }} /></TableCell>
                          {renderQuoteLineTaxCells({
                            isPerLineQuoteTax,
                            row: item,
                            isEditing: true,
                            disabled: quoteLocked,
                            onLineTaxPatch: (patch) => handleNewQuoteLineTaxPatch(index, patch),
                            computeSubtotal: computeQuoteLineSubtotal
                          })}
                          <TableCell sx={{ width: 76, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'right' }}>{formatCurrency(quoteLineSubtotalColumnAmount(item), '$0.00')}</TableCell>
                          <TableCell sx={{ width: 220, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}><Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: '100%' }}><TextField size="small" value={item.notes || ''} onChange={(e) => handleLineItemChange(index, 'notes', e.target.value)} sx={{ flex: 1, minWidth: 0 }} /></Box></TableCell>
                          <TableCell sx={{ width: 80, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}><IconButton size="small" color="primary" onClick={() => openLineItemPopoutForNew(index)} title="Pop out full line item"><OpenInNew fontSize="small" /></IconButton><IconButton size="small" color="error" onClick={() => handleRemoveLineItem(index)} disabled={quoteLocked}><Delete fontSize="small" /></IconButton></TableCell>
                        </TableRow>
                      );
                    })}

                    {(existingPartItems.length > 0 || existingServiceItems.length > 0 || newLineItems.length > 0) && (
                      <>
                        <TableRow className="wm-line-totals-footer wm-line-totals-footer--subtotal" sx={{ bgcolor: '#eceff1' }}>
                          <TableCell colSpan={8} align="left" sx={{ border: '1px solid #e0e0e0', verticalAlign: 'middle' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                              {quoteLineItemsTaxEditActive && !quoteLocked && (
                                <ToggleButtonGroup
                                  exclusive
                                  size="small"
                                  value={quoteTaxScopeToggleValue}
                                  disabled={saving}
                                  onChange={(_, v) => {
                                    if (v != null) void applyQuoteTaxScopePreset(v);
                                  }}
                                  aria-label="Quote tax scope"
                                  sx={{
                                    flexShrink: 0,
                                    '& .MuiToggleButton-root': {
                                      px: 0.75,
                                      py: 0.35,
                                      fontSize: '0.6875rem',
                                      textTransform: 'none',
                                      lineHeight: 1.25
                                    }
                                  }}
                                >
                                  <ToggleButton value="line_items">Tax line items</ToggleButton>
                                  <ToggleButton value="order">Tax order</ToggleButton>
                                  <ToggleButton value="no_tax">No tax</ToggleButton>
                                </ToggleButtonGroup>
                              )}
                              <Typography
                                component="span"
                                sx={{ flex: 1, textAlign: 'right', fontWeight: 700, fontSize: '0.875rem', minWidth: 0 }}
                              >
                                Line items total
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 700, fontSize: '0.875rem', textAlign: 'right' }}>
                            {formatCurrency(quoteLineItemsSubtotalTotal, '$0.00')}
                          </TableCell>
                          <TableCell colSpan={2} sx={{ border: '1px solid #e0e0e0' }} />
                        </TableRow>
                        <TableRow className="wm-line-totals-footer" sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell colSpan={8} align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 600, fontSize: '0.875rem', verticalAlign: 'middle', textAlign: 'right', py: 0.5 }}>
                            {isPerLineQuoteTax ? 'Line Items Tax Total' : 'Tax Total'}
                          </TableCell>
                          <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 600, fontSize: '0.875rem', verticalAlign: 'middle', textAlign: 'right', py: 0.5 }}>
                            {formatCurrency(displayTaxAmount, '$0.00')}
                          </TableCell>
                          <TableCell colSpan={2} sx={{ border: '1px solid #e0e0e0', verticalAlign: 'middle', py: 0.5 }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'center', py: 0 }}>
                              {taxDraft.mode === 'percent' && (
                                <TextField
                                  margin="none"
                                  size="small"
                                  aria-label="Tax rate percent"
                                  sx={documentFooterTaxRatePercentFieldSx}
                                  value={taxDraft.percentStr}
                                  disabled={quoteLocked || isPerLineQuoteTax}
                                  onChange={(e) =>
                                    setTaxDraft((d) => ({
                                      ...d,
                                      percentStr: sanitizeTaxPercentInputValue(e.target.value)
                                    }))
                                  }
                                  onBlur={() => {
                                    const next = {
                                      ...taxDraft,
                                      percentStr:
                                        String(taxDraft.percentStr ?? '').trim() === ''
                                          ? ''
                                          : formatTaxPercentDisplayValue(taxDraft.percentStr)
                                    };
                                    setTaxDraft(next);
                                    void saveTaxDraft(next);
                                  }}
                                  inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end" disableTypography sx={{ ml: 0, mr: 0 }}>
                                        <Box component="span" sx={{ fontSize: '0.8125rem', lineHeight: 1, userSelect: 'none' }}>
                                          %
                                        </Box>
                                      </InputAdornment>
                                    )
                                  }}
                                />
                              )}
                              {taxDraft.mode === 'fixed' && (
                                <TextField
                                  margin="none"
                                  size="small"
                                  label="Amount"
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ width: 104, ...documentFooterTaxFieldSx }}
                                  value={taxDraft.fixedStr}
                                  disabled={quoteLocked || isPerLineQuoteTax}
                                  onChange={(e) => setTaxDraft((d) => ({ ...d, fixedStr: e.target.value }))}
                                  onBlur={() => saveTaxDraft()}
                                  inputProps={{ min: 0, step: 0.01 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow className="wm-line-totals-footer" sx={{ bgcolor: '#eceff1' }}>
                          <TableCell colSpan={8} align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 700, fontSize: '0.875rem', textAlign: 'right' }}>
                            Total (incl. tax)
                          </TableCell>
                          <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 700, fontSize: '0.875rem', textAlign: 'right' }}>
                            {formatCurrency(displayGrandTotal, '$0.00')}
                          </TableCell>
                          <TableCell colSpan={2} sx={{ border: '1px solid #e0e0e0' }} />
                        </TableRow>
                      </>
                    )}

                    {existingPartItems.length === 0 && existingServiceItems.length === 0 && newLineItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ border: '1px solid #e0e0e0', py: 3 }}>
                          No line items. Click "+ Add Part" or "+ Add Service" to add items.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
              )}
            </CardContent>
          </Card>
          )}

          {!quoteLocked && quote.status === 'open' && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" color="primary" size="large" onClick={handleConvertToOrder} sx={{ textTransform: 'none' }}>
                Create Order
              </Button>
            </Box>
          )}

          {relatedOrders.length > 0 && (
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Linked orders ({relatedOrders.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                {relatedOrders.slice(0, 6).map((ord) => (
                  <Button
                    key={ord.id}
                    component={Link}
                    to={`/work-orders/${ord.id}`}
                    variant="outlined"
                    size="small"
                  >
                    {ord.identifier ?? ord.work_order_number ?? `#${ord.id}`}
                  </Button>
                ))}
              </Box>
              {relatedOrders.length > 6 && (
                <Button size="small" sx={{ mt: 1 }} onClick={() => setActiveTab(3)}>
                  View all {relatedOrders.length} in Order History
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Card sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                Customer Contacts ({contacts.length})
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setContactsModalOpen(true)}
              >
                Manage Contacts
              </Button>
            </Box>
            {loadingContacts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : contacts.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No contacts found. Click "Manage Contacts" to add contacts for this customer.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contacts.map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell>
                          {contact.name}
                          {contact.is_ap_contact && (
                            <Chip label="AP" size="small" color="primary" sx={{ ml: 0.5, height: '18px', fontSize: '0.7rem' }} />
                          )}
                        </TableCell>
                        <TableCell>{contact.title || '-'}</TableCell>
                        <TableCell>
                          {contact.phone_number || '-'}
                          {contact.ext && ` x${contact.ext}`}
                        </TableCell>
                        <TableCell>{contact.email_address || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={contact.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={contact.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 1.5 }}>
            {(relatedOrders?.length ?? 0) > 0 ? (
              <Box
                sx={{
                  mb: 1.5,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'grey.50'
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Attachments are locked once the quote is converted to an order. Add or remove attachments from the order page.
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>
                  Attachments: {attachments.length}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  mb: 1.5,
                  minHeight: 120,
                  p: 2,
                  border: '2px dashed',
                  borderColor: isDraggingOverAttachment ? 'primary.main' : 'grey.400',
                  borderRadius: 2,
                  bgcolor: isDraggingOverAttachment ? 'action.hover' : 'grey.50',
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                  transition: 'border-color 0.15s ease, background-color 0.15s ease'
                }}
                onDragOver={handleAttachmentDragOver}
                onDragLeave={handleAttachmentDragLeave}
                onDrop={handleAttachmentDrop}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CloudUpload sx={{ fontSize: 40, opacity: isDraggingOverAttachment ? 1 : 0.6 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Drop files here
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      or use the Upload button · max 5 files, 10 MB each
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Attachments: {attachments.length}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  component="label"
                  size="small"
                  startIcon={<AddIcon />}
                >
                  Upload
                  <input
                    type="file"
                    hidden
                    multiple
                    onChange={handleFileInputChange}
                  />
                </Button>
              </Box>
            )}
            {attachmentsError && (
              <Alert severity="error" sx={{ mb: 1 }}>{attachmentsError}</Alert>
            )}
            {attachmentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : attachments.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                {(relatedOrders?.length ?? 0) > 0
                  ? 'No attachments.'
                  : 'No attachments yet. Drag and drop files onto the area above or use the Upload button.'}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>File Name</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Uploaded At</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attachments.map((att) => (
                      <TableRow key={att.id} hover>
                        <TableCell>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadAttachment(att);
                            }}
                            size="small"
                            sx={{ textTransform: 'none' }}
                          >
                            {att.file_name}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {att.file_size != null
                            ? `${(att.file_size / 1024 / 1024).toFixed(2)} MB`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {att.uploaded_at
                            ? format(new Date(att.uploaded_at), 'MM/dd/yyyy HH:mm')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {(relatedOrders?.length ?? 0) > 0 ? (
                            <Typography variant="caption" color="text.secondary">Locked</Typography>
                          ) : (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteAttachment(att.id)}
                              aria-label="Delete attachment"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
      {activeTab === 3 && (
        <Card sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 1.5 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1 }}>
              Order History ({relatedOrders.length})
            </Typography>
            {loadingOrders ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : relatedOrders.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No orders created from this quote yet.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order #</TableCell>
                      <TableCell>PO #</TableCell>
                      <TableCell>Order Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {relatedOrders.map((order) => (
                      <TableRow key={order.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/work-orders/${order.id}`)}>
                        <TableCell>{order.identifier ?? order.work_order_number}</TableCell>
                        <TableCell>{order.po_number || '-'}</TableCell>
                        <TableCell>{order.order_date ? formatDateDisplay(order.order_date) : '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.status || 'open'}
                            size="small"
                            color={order.status === 'completed' ? 'success' : order.status === 'cancelled' ? 'error' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{formatCurrency(order.total_amount, '$0.00')}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/work-orders/${order.id}`);
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 4 && (
        <Card sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <QuoteOrderLineagePanel loading={lineageLoading} error={lineageError} data={lineageData} />
          </CardContent>
        </Card>
      )}

      {activeTab === 5 && (
        <Card>
          <CardContent sx={{ p: 1.5 }}>
            <TextField
              fullWidth
              label="Dependent clause (printed at bottom of quote)"
              value={dependentClause}
              onChange={(e) => setDependentClause(e.target.value)}
              multiline
              rows={4}
              margin="dense"
              size="small"
              helperText="This text appears at the bottom of the printed quote. Edit as needed before printing."
            />
          </CardContent>
        </Card>
      )}

      <Dialog
        open={quotePrintSettingsOpen}
        onClose={() => setQuotePrintSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontSize: '1.125rem',
            fontWeight: 600,
            fontFamily: 'inherit',
            letterSpacing: '-0.01em',
            color: 'text.primary'
          }}
        >
          Print version options
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, fontSize: '0.875rem' }}>
            Use <strong>Print quotation</strong> to open the browser print dialog for the same quotation as the toolbar
            print icon—not this popup. Options below apply only to that document for this print. The toolbar print button uses
            the same default layout (single line-items table; notes and description hidden). Uncheck the boxes below to include
            them. Field Work / Service layout (list style, proposal breakdown, dependent clause) is in{' '}
            <strong>Printed Version Options</strong> on this page and is saved with the quote. Nothing here is saved.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover'
            }}
          >
            <FormControlLabel
              sx={{
                m: 0,
                mr: 0,
                alignItems: 'center',
                gap: 0.75,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: 'text.secondary',
                  fontWeight: 400,
                  paddingTop: 0
                }
              }}
              control={
                <Checkbox
                  size="small"
                  checked={quotePrintSettingsDraft.hideNotes}
                  onChange={(e) => setQuotePrintSettingsDraft((d) => ({ ...d, hideNotes: e.target.checked }))}
                  sx={{ p: 0.75 }}
                  inputProps={{ 'aria-label': 'Hide notes on printed quotation' }}
                />
              }
              label="Hide Notes"
            />
            <FormControlLabel
              sx={{
                m: 0,
                mr: 0,
                alignItems: 'center',
                gap: 0.75,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: 'text.secondary',
                  fontWeight: 400,
                  paddingTop: 0
                }
              }}
              control={
                <Checkbox
                  size="small"
                  checked={quotePrintSettingsDraft.hideDescription}
                  onChange={(e) => setQuotePrintSettingsDraft((d) => ({ ...d, hideDescription: e.target.checked }))}
                  sx={{ p: 0.75 }}
                  inputProps={{ 'aria-label': 'Hide description on printed quotation' }}
                />
              }
              label="Hide Description"
            />
            {!isScopeOfWorkQuote(quote) ? (
              <FormControlLabel
                sx={{
                  m: 0,
                  mr: 0,
                  alignItems: 'flex-start',
                  gap: 0.75,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    color: 'text.secondary',
                    fontWeight: 400,
                    paddingTop: 0
                  }
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={quotePrintSettingsDraft.separateLineSections}
                    onChange={(e) =>
                      setQuotePrintSettingsDraft((d) => ({ ...d, separateLineSections: e.target.checked }))
                    }
                    sx={{ p: 0.75, pt: 0.35 }}
                    inputProps={{ 'aria-label': 'Print parts and services as separate tables' }}
                  />
                }
                label="Separate line items into two tables"
              />
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
          <Button variant="text" color="inherit" onClick={() => setQuotePrintSettingsOpen(false)}>
            Close
          </Button>
          <Button variant="contained" color="primary" startIcon={<Print />} onClick={handlePrintFromQuoteSettingsDialog}>
            Print quotation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Quote Confirmation Dialog */}
      <Dialog
        open={revisionDialogOpen}
        onClose={() => setRevisionDialogOpen(false)}
        aria-labelledby="quote-revision-history-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="quote-revision-history-dialog-title">Previous Quote Revisions</DialogTitle>
        <DialogContent>
          {revisionDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : revisionDialogItems.length === 0 ? (
            <DialogContentText>No previous revisions found.</DialogContentText>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Quote #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {revisionDialogItems.map((rev) => (
                  <TableRow key={rev.id}>
                    <TableCell>{rev.identifier}</TableCell>
                    <TableCell>{rev.quote_date ? formatDateDisplay(rev.quote_date) : '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          String(rev.status || 'draft').toLowerCase() === 'deprecated'
                            ? 'revised'
                            : String(rev.status || 'draft').toLowerCase()
                        }
                        size="small"
                        color={getStatusColor(rev.status)}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>{formatCurrency(rev.total_amount, '—')}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setRevisionDialogOpen(false);
                          navigate(`/quotes/${rev.id}`);
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevisionDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-quote-dialog-title"
        aria-describedby="delete-quote-dialog-description"
      >
        <DialogTitle id="delete-quote-dialog-title">
          Delete Quote {quote ? formatQuoteNumber(quote) : ''}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-quote-dialog-description">
            Are you sure you want to delete quote {quote ? formatQuoteNumber(quote) : ''}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDeleteQuote} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Line Item Confirmation Dialog */}
      <Dialog
        open={deleteLineItemDialogOpen}
        onClose={() => {
          setDeleteLineItemDialogOpen(false);
          setLineItemToDelete(null);
        }}
        aria-labelledby="delete-line-item-dialog-title"
        aria-describedby="delete-line-item-dialog-description"
      >
        <DialogTitle id="delete-line-item-dialog-title">
          Delete Line Item?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-line-item-dialog-description">
            Are you sure you want to delete this line item? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteLineItemDialogOpen(false);
              setLineItemToDelete(null);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={confirmDeleteLineItem} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full Line Item Popout Dialog */}
      <Dialog
        open={lineItemPopoutOpen}
        onClose={closeLineItemPopout}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Line Item</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1.5, display: 'grid', gap: 1.25 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '80% 20%' }, width: '100%' }}>
              <FormControl size="small" fullWidth sx={{ gridColumn: '1 / 2' }}>
                <InputLabel>Part</InputLabel>
                <Select
                  label="Part"
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        width: 'min(640px, 90vw)'
                      }
                    }
                  }}
                  value={
                    (lineItemPopoutDraft?.item_type || 'part') === 'service'
                      ? `service:${lineItemPopoutDraft?.service_id ?? ''}`
                      : `part:${lineItemPopoutDraft?.part_id ?? ''}`
                  }
                  onChange={(e) => handleLineItemPopoutRefChange(e.target.value)}
                >
                  {parts.map((part) => (
                    <MenuItem
                      key={`detail-pop-all-part-${part.id}`}
                      value={`part:${part.id}`}
                      title={formatPartOptionLabel(part)}
                      sx={{ maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {formatPartOptionLabel(part)}
                    </MenuItem>
                  ))}
                  {services.map((service) => (
                    <MenuItem
                      key={`detail-pop-all-svc-${service.id}`}
                      value={`service:${service.id}`}
                      title={formatServiceOptionLabel(service)}
                      sx={{ maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {formatServiceOptionLabel(service)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Type"
                value={(lineItemPopoutDraft?.item_type || 'part') === 'service' ? 'Service' : 'Part'}
                InputProps={{ readOnly: true }}
                sx={{ width: 120 }}
              />
              <TextField
                size="small"
                label="Code"
                value={
                  (lineItemPopoutDraft?.item_type || 'part') === 'service'
                    ? effectiveServiceCode(
                        services.find((s) => String(s.id) === String(lineItemPopoutDraft?.service_id)) || {
                          id: lineItemPopoutDraft?.service_id,
                          service_code: lineItemPopoutDraft?.service_code
                        }
                      )
                    : (
                      lineItemPopoutDraft?.part_number
                      || parts.find((p) => String(p.id) === String(lineItemPopoutDraft?.part_id))?.part_number
                      || ''
                    )
                }
                InputProps={{ readOnly: true }}
                sx={{ width: 140 }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
              <TextField
                size="small"
                type={(lineItemPopoutDraft?.item_type || 'part') === 'service' ? 'text' : 'number'}
                inputMode={(lineItemPopoutDraft?.item_type || 'part') === 'service' ? 'decimal' : 'numeric'}
                label="Quantity"
                value={
                  (lineItemPopoutDraft?.item_type || 'part') === 'service'
                    ? formatLineItemQtyEditValue('service', lineItemPopoutDraft?.quantity)
                    : formatLineItemQtyEditValue('part', lineItemPopoutDraft?.quantity)
                }
                onChange={(e) => {
                  const v = e.target.value;
                  const it = lineItemPopoutDraft?.item_type || 'part';
                  if (it === 'service') {
                    setLineItemPopoutDraft((prev) => ({ ...(prev || {}), quantity: v }));
                    return;
                  }
                  const t = String(v).trim();
                  if (t === '' || t === '-') {
                    setLineItemPopoutDraft((prev) => ({ ...(prev || {}), quantity: '' }));
                    return;
                  }
                  const n = Math.max(0, Math.trunc(Math.round(parseFloat(t))));
                  setLineItemPopoutDraft((prev) => ({ ...(prev || {}), quantity: String(n) }));
                }}
                inputProps={{
                  min: 0,
                  step: (lineItemPopoutDraft?.item_type || 'part') === 'service' ? undefined : 1,
                  style: { textAlign: 'center' }
                }}
                sx={{ width: 120 }}
              />
              <TextField
                size="small"
                label="Unit"
                value={lineItemPopoutDraft?.unit || ((lineItemPopoutDraft?.item_type || 'part') === 'service' ? 'HR' : 'EA')}
                onChange={(e) => setLineItemPopoutDraft((prev) => ({ ...(prev || {}), unit: e.target.value }))}
                sx={{ width: 120 }}
              />
              <TextField
                size="small"
                type="number"
                label="Unit Price"
                value={lineItemPopoutDraft?.unit_price ?? ''}
                onChange={(e) => setLineItemPopoutDraft((prev) => ({ ...(prev || {}), unit_price: e.target.value }))}
                inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                sx={{ width: 140 }}
              />
              <TextField
                size="small"
                label="Subtotal"
                value={formatCurrency((Number(lineItemPopoutDraft?.quantity) || 0) * (Number(lineItemPopoutDraft?.unit_price) || 0), '$0.00')}
                InputProps={{ readOnly: true }}
                sx={{ width: 140 }}
              />
            </Box>
            {isPerLineQuoteTax && lineItemPopoutDraft ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  type="number"
                  aria-label="Line tax percent"
                  value={lineTaxPercentFieldValue(lineItemPopoutDraft, computeQuoteLineSubtotal(lineItemPopoutDraft))}
                  onChange={(e) => {
                    const sub = computeQuoteLineSubtotal(lineItemPopoutDraft);
                    const patch = lineTaxPatchFromPercentInput(sub, e.target.value);
                    if (patch) setLineItemPopoutDraft((prev) => ({ ...(prev || {}), ...patch }));
                  }}
                  onKeyDown={(e) => {
                    const sub = computeQuoteLineSubtotal(lineItemPopoutDraft);
                    handleLineTaxPercentArrowKeyDown(e, lineItemPopoutDraft, sub, (patch) =>
                      setLineItemPopoutDraft((prev) => ({ ...(prev || {}), ...patch }))
                    );
                  }}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    style: { textAlign: 'right' },
                    ...lineTaxPercentInputPropsExtra()
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" disableTypography sx={{ mr: 0.25 }}>
                        <Box component="span" sx={{ fontSize: '0.8125rem', lineHeight: 1 }}>
                          %
                        </Box>
                      </InputAdornment>
                    )
                  }}
                  sx={{ width: 88, maxWidth: 96, ...lineItemTaxPercentEditFieldSx }}
                />
                <CurrencyTextField
                  size="small"
                  aria-label="Line tax amount"
                  value={(() => {
                    const sub = computeQuoteLineSubtotal(lineItemPopoutDraft);
                    const mode = normalizeLineTaxModeUi(lineItemPopoutDraft.line_tax_mode);
                    const amt = Math.round((Number(computeLineItemTaxAmount(sub, lineItemPopoutDraft)) || 0) * 100) / 100;
                    return mode === 'none' && amt === 0 ? '' : amt;
                  })()}
                  onChange={(e) => {
                    const sub = computeQuoteLineSubtotal(lineItemPopoutDraft);
                    const patch = lineTaxPatchFromAmountInput(sub, e.target.value);
                    if (patch) setLineItemPopoutDraft((prev) => ({ ...(prev || {}), ...patch }));
                  }}
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                  dollarAdornment
                  inputProps={{ style: { textAlign: 'right' } }}
                  sx={{ width: 200, minWidth: 180, ...lineItemTaxDollarsEditFieldSx }}
                />
              </Box>
            ) : null}
            <TextField
              size="small"
              label="Description"
              multiline
              rows={4}
              value={lineItemPopoutDraft?.description || ''}
              onChange={(e) => setLineItemPopoutDraft((prev) => ({ ...(prev || {}), description: e.target.value }))}
              sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}
            />
            <TextField
              size="small"
              label="Notes"
              multiline
              rows={3}
              value={lineItemPopoutDraft?.notes || ''}
              onChange={(e) => setLineItemPopoutDraft((prev) => ({ ...(prev || {}), notes: e.target.value }))}
              sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLineItemPopout}>Cancel</Button>
          <Button variant="contained" onClick={saveLineItemPopout} color="primary" disabled={!lineItemPopoutDraft}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={regenerateOrderPromptOpen}
        onClose={closeRegenerateOrderPrompt}
        aria-labelledby="regenerate-order-dialog-title"
      >
        <DialogTitle id="regenerate-order-dialog-title">
          Regenerate Order from Revised Quote?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are now viewing the revised quote. Would you like to regenerate the linked order now?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRegenerateOrderPrompt} color="inherit">
            Not now
          </Button>
          <Button onClick={confirmRegenerateOrderFromPrompt} variant="contained" color="primary" disabled={saving}>
            Regenerate Order
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={quoteEditRelatedWarningOpen}
        onClose={() => !saving && setQuoteEditRelatedWarningOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Orders linked to this quote</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 1 }}>
              {relatedOrders.length} order{relatedOrders.length === 1 ? '' : 's'} linked to this quote. Editing the quote does not change existing
              orders.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Continue only if you intend to update the quote for documentation or future work; line items and totals on linked orders stay as they are
              until you edit each order directly.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setQuoteEditRelatedWarningOpen(false)} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={confirmQuoteEditDespiteRelatedOrders} variant="contained" color="primary" autoFocus disabled={saving}>
            Edit anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Order Confirmation Dialog */}
      <Dialog
        open={convertDialogOpen}
        onClose={() => setConvertDialogOpen(false)}
        aria-labelledby="convert-order-dialog-title"
        aria-describedby="convert-order-dialog-description"
      >
        <DialogTitle id="convert-order-dialog-title">
          Create order from quote?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="convert-order-dialog-description">
            Create an order from Quote #{quote ? formatQuoteNumber(quote) : ''}? The new order will be linked to this quote; the quote status is not changed automatically.
            {quote && isScopeOfWorkQuote(quote) ? (
              <>
                {' '}
                For Field Work / Service, the order will use your proposal total as a single lump-sum service line; scope and clarifications are copied into the order
                description and notes.
              </>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmConvertToOrder} color="primary" variant="contained" autoFocus disabled={saving}>
            Create order
          </Button>
          </DialogActions>
        </Dialog>

        {/* Customer Contacts Modal */}
        <CustomerContactsModal
          open={contactsModalOpen}
          onClose={() => {
            setContactsModalOpen(false);
            if (activeTab === 1) {
              loadContacts(); // Reload contacts when modal closes
            }
          }}
          customerId={quote?.customer_id}
          customerName={quote?.company_name || `${quote?.customer_first_name || ''} ${quote?.customer_last_name || ''}`.trim() || 'Customer'}
        />

        {/* New Part Modal */}
        <NewPartModal
          open={newPartModalOpen}
          onClose={() => {
            setNewPartModalOpen(false);
            setNewPartModalRowIndex(null);
            setNewPartModalItemId(null);
          }}
          onCreated={handleNewPartCreated}
        />

        <NewServiceModal
          open={newServiceModalOpen}
          onClose={() => {
            setNewServiceModalOpen(false);
            setNewServiceModalRowIndex(null);
            setNewServiceModalItemId(null);
          }}
          onCreated={handleNewServiceCreated}
        />
      </Box>
    </>
  );
};

export default QuoteDetail;
